import React, { useState, useEffect, useRef } from 'react';
import GoogleMapReact from 'google-map-react';
import axios from 'axios';
import './Marker.css';
import config from '../../config';
import { useGlobalContext } from '../../context';
import Polyline from './Polylines';
import Polygon from './Polygons';
import defaultDriverProfile from '../../assets/img/default_driver_profile.png';
import DriverBadgeCard from '../Shared/DriverBadgeCard';
import { greenFlagIcon, whiteFlagIcon, checkeredFlagIcon, trophyIcon } from '../Shared/MapIcons';
import {
    getDriverVehicleRenderCandidates,
    getDriverVehicleRenderUrl,
    getVehicleRenderSharedAssetIds,
    getVehicleRenderUrl,
    getVehicleRenderCandidates,
    isSignedAssetUrl,
    normalizeAssetUrl,
    resolveDriverActiveVehicle,
    resolveDriverVehicle,
} from '../../utils/vehicleImage';
import { loadGoogleMapsApi } from '../Helpers/GoogleMapAutoComplete';
import * as user_urls from '../../urls/api-users/admin';

const PRODUCTION_USERS_HOST = 'https://api-users.gophr.app';
const LOCAL_USERS_HASURA_PROXY_PATH = '/__local/users-hasura';
const loggedVehicleImageErrorKeys = new Set();

const loadDispatchGoogleMaps = () => loadGoogleMapsApi().then(google => google.maps);

const MAP_CENTER_EPSILON = 0.000001;
const MAP_BOUNDS_EPSILON = 0.000001;
const MAP_BOUNDS_PADDING_RATIO = 0.12;
const CLOSED_ROUTE_STATUSES = new Set(['route_completed', 'route_cancelled']);
const ROUTE_BREADCRUMB_VISIBLE_STATUSES = new Set(['route_assigned', 'route_claimed', 'route_enroute', 'route_completed']);
const MAX_OFFLINE_DRIVER_GEOCODES_PER_RENDER = 150;
const OFFLINE_DRIVER_INTERACTIVE_RADIUS_MILES = 50;
const OFFLINE_DRIVER_CLUSTER_CELL_SIZE_BY_ZOOM = [
    { maxZoom: 7, cellSize: 1.4 },
    { maxZoom: 8, cellSize: 0.75 },
    { maxZoom: 9, cellSize: 0.35 },
    { maxZoom: 10, cellSize: 0.18 },
    { maxZoom: 11, cellSize: 0.09 },
    { maxZoom: 12, cellSize: 0.045 },
];

const getOfflineDriverClusterCellSize = zoom => {
    const numericZoom = Number.isFinite(Number(zoom)) ? Number(zoom) : 8;
    const matchedCellSize = OFFLINE_DRIVER_CLUSTER_CELL_SIZE_BY_ZOOM.find(option => numericZoom <= option.maxZoom);

    return matchedCellSize ? matchedCellSize.cellSize : 0.03;
};

const getOfflineClusterMarkerScale = count => {
    if (count >= 100) {
        return 18;
    }

    if (count >= 50) {
        return 16;
    }

    if (count >= 20) {
        return 14;
    }

    if (count >= 10) {
        return 12;
    }

    return 10;
};

const toRadians = degrees => (Number(degrees) * Math.PI) / 180;

const getDistanceMiles = (startLat, startLng, endLat, endLng) => {
    const earthRadiusMiles = 3958.8;
    const deltaLat = toRadians(endLat - startLat);
    const deltaLng = toRadians(endLng - startLng);
    const startLatRadians = toRadians(startLat);
    const endLatRadians = toRadians(endLat);

    const haversineValue = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
        + Math.cos(startLatRadians) * Math.cos(endLatRadians) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const angularDistance = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));

    return earthRadiusMiles * angularDistance;
};

const getVisibleBoundsRadiusMiles = bounds => {
    if (!bounds) {
        return Number.POSITIVE_INFINITY;
    }

    const north = Number(bounds.north);
    const south = Number(bounds.south);
    const east = Number(bounds.east);
    const west = Number(bounds.west);

    if (![north, south, east, west].every(Number.isFinite)) {
        return Number.POSITIVE_INFINITY;
    }

    const centerLat = (north + south) / 2;
    const centerLng = (east + west) / 2;
    const northRadius = getDistanceMiles(centerLat, centerLng, north, centerLng);
    const eastRadius = getDistanceMiles(centerLat, centerLng, centerLat, east);

    return Math.max(northRadius, eastRadius);
};

const shouldExpandOfflineDriversForBounds = bounds => getVisibleBoundsRadiusMiles(bounds) <= OFFLINE_DRIVER_INTERACTIVE_RADIUS_MILES;

const getUsersHasuraHeaders = () => {
    const usersHasuraAdminSecret = config.config.api_users.admin.usersHasuraAdminSecret;

    if (usersHasuraAdminSecret) {
        return {
            'content-type': 'application/json',
            'x-hasura-admin-secret': usersHasuraAdminSecret,
        };
    }

    return {
        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
        'content-type': 'application/json',
    };
};

const isLocalLikeDispatchHost = hostValue => {
    const normalizedHost = String(hostValue || '').trim().toLowerCase();

    return normalizedHost.includes('localhost') || normalizedHost.includes('127.0.0.1');
};

const getVehicleImageAssetLookupTargets = () => {
    const targets = [];

    if (typeof window !== 'undefined' && window.location && isLocalLikeDispatchHost(window.location.hostname)) {
        targets.push({
            url: LOCAL_USERS_HASURA_PROXY_PATH,
            headers: {
                'content-type': 'application/json',
            },
        });
    }

    if (user_urls.hasuraHost) {
        targets.push({
            url: user_urls.hasuraHost,
            headers: getUsersHasuraHeaders(),
        });
    }

    return targets.filter((target, index, collection) => {
        return target && target.url && collection.findIndex(candidate => candidate && candidate.url === target.url) === index;
    });
};

const requestUsersHasuraWithFallback = async ({ query, variables, timeoutMs = 5000 }) => {
    let lastError = null;

    for (const target of getVehicleImageAssetLookupTargets()) {
        try {
            return await axios({
                method: 'post',
                timeout: target.url === LOCAL_USERS_HASURA_PROXY_PATH ? Math.max(timeoutMs, 8000) : timeoutMs,
                headers: target.headers,
                url: target.url,
                data: {
                    query,
                    variables,
                },
            });
        } catch (error) {
            lastError = error;
        }
    }

    throw lastError || new Error('Users Hasura request failed');
};

const getShipmentFeeBlob = shipment => (
    shipment && shipment.shipping_fee_blob && typeof shipment.shipping_fee_blob === 'object'
        ? shipment.shipping_fee_blob
        : {}
);

const isFedexShipment = shipment => getShipmentFeeBlob(shipment).is_fedex === true;

const normalizeMapCorner = corner => {
    if (!(corner && Number.isFinite(Number(corner.lat)) && Number.isFinite(Number(corner.lng)))) {
        return null;
    }

    return {
        lat: typeof corner.lat === 'number' ? corner.lat : parseFloat(corner.lat),
        lng: typeof corner.lng === 'number' ? corner.lng : parseFloat(corner.lng),
    };
};

const normalizeVisibleMapBounds = rawBounds => {
    if (!rawBounds) {
        return null;
    }

    const directNorth = Number.isFinite(Number(rawBounds.north)) ? Number(rawBounds.north) : null;
    const directSouth = Number.isFinite(Number(rawBounds.south)) ? Number(rawBounds.south) : null;
    const directEast = Number.isFinite(Number(rawBounds.east)) ? Number(rawBounds.east) : null;
    const directWest = Number.isFinite(Number(rawBounds.west)) ? Number(rawBounds.west) : null;

    if (directNorth !== null && directSouth !== null && directEast !== null && directWest !== null) {
        return {
            north: directNorth,
            south: directSouth,
            east: directEast,
            west: directWest,
        };
    }

    const cornerPairs = [
        normalizeMapCorner(rawBounds.nw),
        normalizeMapCorner(rawBounds.ne),
        normalizeMapCorner(rawBounds.sw),
        normalizeMapCorner(rawBounds.se),
        normalizeMapCorner(rawBounds.ne),
        normalizeMapCorner(rawBounds.sw),
    ].filter(Boolean);

    if (cornerPairs.length === 0) {
        return null;
    }

    return {
        north: Math.max(...cornerPairs.map(corner => corner.lat)),
        south: Math.min(...cornerPairs.map(corner => corner.lat)),
        east: Math.max(...cornerPairs.map(corner => corner.lng)),
        west: Math.min(...cornerPairs.map(corner => corner.lng)),
    };
};

const padVisibleMapBounds = (bounds, paddingRatio = MAP_BOUNDS_PADDING_RATIO) => {
    if (!bounds) {
        return null;
    }

    const latSpan = Math.max(Math.abs(bounds.north - bounds.south), 0.01);
    const lngSpan = Math.max(Math.abs(bounds.east - bounds.west), 0.01);
    const latPadding = latSpan * paddingRatio;
    const lngPadding = lngSpan * paddingRatio;

    return {
        north: Math.min(90, bounds.north + latPadding),
        south: Math.max(-90, bounds.south - latPadding),
        east: Math.min(180, bounds.east + lngPadding),
        west: Math.max(-180, bounds.west - lngPadding),
    };
};

const areVisibleMapBoundsEqual = (left, right) => {
    if (!left && !right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    return Math.abs(left.north - right.north) < MAP_BOUNDS_EPSILON
        && Math.abs(left.south - right.south) < MAP_BOUNDS_EPSILON
        && Math.abs(left.east - right.east) < MAP_BOUNDS_EPSILON
        && Math.abs(left.west - right.west) < MAP_BOUNDS_EPSILON;
};

const isCoordinateWithinVisibleBounds = (coordinate, bounds) => {
    if (!coordinate || !bounds) {
        return false;
    }

    const latitude = Number(coordinate.lat);
    const longitude = Number(coordinate.lng);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
    }

    const longitudeIsVisible = bounds.west <= bounds.east
        ? longitude >= bounds.west && longitude <= bounds.east
        : longitude >= bounds.west || longitude <= bounds.east;

    return latitude >= bounds.south && latitude <= bounds.north && longitudeIsVisible;
};

const normalizeDriverStatusFlag = value => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (['true', '1', 'yes', 'y', 'on', 'online', 'available'].includes(normalizedValue)) {
            return true;
        }

        if (['false', '0', 'no', 'n', 'off', 'offline', 'unavailable', 'enroute', 'busy'].includes(normalizedValue)) {
            return false;
        }
    }

    return Boolean(value);
};


const getComparableTimestamp = value => {
    const timestamp = value ? new Date(value).getTime() : Number.NaN;
    return Number.isFinite(timestamp) ? timestamp : Number.NaN;
};

// Newest timestamp among a driver's native-app location actions (LastDriverActions), used to
// compare app-sourced position freshness against the web-link LocationUpdate breadcrumbs.
const getDriverLiveLocationTimestamp = driver => {
    if (!driver) {
        return Number.NaN;
    }

    const driverActions = Array.isArray(driver.LastDriverActions)
        ? driver.LastDriverActions
        : (driver.LastDriverActions ? [driver.LastDriverActions] : []);

    return driverActions.reduce((newest, action) => {
        if (!action || !action.location_blob) {
            return newest;
        }
        const ts = getComparableTimestamp(action.timestamp);
        return Number.isFinite(ts) && (!Number.isFinite(newest) || ts > newest) ? ts : newest;
    }, Number.NaN);
};

const isClosedRoute = route => Boolean(route && CLOSED_ROUTE_STATUSES.has(route.status));

const getCurrentRouteAssignmentTimestamp = route => {
    const routeDrivers = route && Array.isArray(route.RouteDrivers) ? route.RouteDrivers : [];
    const activeDriverAssignments = routeDrivers.filter(routeDriver => routeDriver && routeDriver.status === 'driver');

    return activeDriverAssignments.reduce((latestTimestamp, routeDriver) => {
        const assignmentTimestamp = getComparableTimestamp(routeDriver.created_at);
        return Number.isFinite(assignmentTimestamp) && assignmentTimestamp > latestTimestamp
            ? assignmentTimestamp
            : latestTimestamp;
    }, Number.NaN);
};

const filterRouteLocationUpdates = (route, locationUpdates) => {
    if (!Array.isArray(locationUpdates)) {
        return [];
    }

    const assignedAtMs = getCurrentRouteAssignmentTimestamp(route);
    const completedAtMs = getComparableTimestamp(route && route.completed_at);

    return locationUpdates.filter(locationUpdate => {
        const timestampMs = getComparableTimestamp(locationUpdate && locationUpdate.timestamp);
        return Number.isFinite(timestampMs)
            && (!Number.isFinite(assignedAtMs) || timestampMs >= assignedAtMs)
            && (!Number.isFinite(completedAtMs) || timestampMs <= completedAtMs);
    });
};

const filterRouteTrackingDetails = (route, trackingDetails) => {
    if (!Array.isArray(trackingDetails)) {
        return [];
    }

    const assignedAtMs = getCurrentRouteAssignmentTimestamp(route);
    const completedAtMs = getComparableTimestamp(route && route.completed_at);

    return trackingDetails.filter(track => {
        const timestampMs = getComparableTimestamp(track && (track.time || track.timestamp));
        return Number.isFinite(timestampMs)
            && (!Number.isFinite(assignedAtMs) || timestampMs >= assignedAtMs)
            && (!Number.isFinite(completedAtMs) || timestampMs <= completedAtMs);
    });
};

const isClosedWaypoint = waypoint => Boolean(
    waypoint && (waypoint.is_waypoint_completed === true || waypoint.completed_at)
);

const hasFiniteCoordinate = value => {
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(numericValue);
};

const normalizeWaypointCoordinates = waypoint => {
    if (!(waypoint && waypoint.address)) {
        return null;
    }

    const lat = typeof waypoint.address.lat === 'number' ? waypoint.address.lat : parseFloat(waypoint.address.lat);
    const lng = typeof waypoint.address.lng === 'number' ? waypoint.address.lng : parseFloat(waypoint.address.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }

    return {
        ...waypoint,
        address: {
            ...waypoint.address,
            lat,
            lng,
        },
    };
};

const hasRenderableBounds = route => Boolean(
    route &&
        route.bounds &&
        hasFiniteCoordinate(route.bounds.min_lat) &&
        hasFiniteCoordinate(route.bounds.min_lng) &&
        hasFiniteCoordinate(route.bounds.max_lat) &&
        hasFiniteCoordinate(route.bounds.max_lng)
);

const getVisibleRoutePath = (route, includeCompleted = false) => {
    if (!route || !Array.isArray(route.path)) {
        return [];
    }

    const visibleWaypoints = includeCompleted ? route.path : route.path.filter(waypoint => !isClosedWaypoint(waypoint));

    return visibleWaypoints
        .map(normalizeWaypointCoordinates)
        .filter(Boolean);
};

const getRenderableRoute = (route, includeCompleted = false) => {
    if (!route) {
        return null;
    }

    const visiblePath = getVisibleRoutePath(route, includeCompleted);

    if (visiblePath.length === 0) {
        return null;
    }

    return {
        ...route,
        path: visiblePath,
    };
};

const areCoordinatesEqual = (left, right) => {
    if (!left || !right) {
        return false;
    }

    return Math.abs(left.lat - right.lat) < MAP_CENTER_EPSILON && Math.abs(left.lng - right.lng) < MAP_CENTER_EPSILON;
};

const RAIN_VIEWER_MANIFEST_URL = 'https://api.rainviewer.com/public/weather-maps.json';
const RAIN_VIEWER_TILE_HOST = 'https://tilecache.rainviewer.com';
const WEATHER_OVERLAY_REFRESH_MS = 10 * 60 * 1000;
const WEATHER_TILE_SIZE = 256;
const WEATHER_COLOR_SCHEME = 6;
const WEATHER_SMOOTHING = 1;
const WEATHER_SNOW = 1;

const getLatestWeatherTilePath = manifest => {
    const nowcastFrames = Array.isArray(manifest && manifest.radar && manifest.radar.nowcast) ? manifest.radar.nowcast : [];
    const pastFrames = Array.isArray(manifest && manifest.radar && manifest.radar.past) ? manifest.radar.past : [];
    const frames = nowcastFrames.length > 0 ? nowcastFrames : pastFrames;
    const latestFrame = frames.length > 0 ? frames[frames.length - 1] : null;

    return latestFrame && latestFrame.path ? latestFrame.path : null;
};

const Marker = props => {
    const { color, name, iconType, isCompleted } = props;

    if (iconType === 'pickup') {
        const pickupIcon = isCompleted ? greenFlagIcon : whiteFlagIcon;
        return <img src={pickupIcon} alt="Pickup" title={name} style={{ width: 24, height: 24, cursor: 'pointer', transform: 'translate(-12px, -24px)', position: 'relative', zIndex: 1 }} />;
    } else if (iconType === 'dropoff') {
        return <img src={checkeredFlagIcon} alt="Dropoff" title={name} style={{ width: 24, height: 24, cursor: 'pointer', transform: 'translate(-12px, -24px)', position: 'relative', zIndex: 1 }} />;
    } else if (iconType === 'completed') {
        return <img src={trophyIcon} alt="Completed" title={name} style={{ width: 24, height: 24, cursor: 'pointer', transform: 'translate(-12px, -24px)', position: 'relative', zIndex: 1 }} />;
    }

    // Fallback to old circular marker
    return <div className="marker" style={{ backgroundColor: color, cursor: 'pointer', zIndex: 1 }} title={name} />;
};

// Waypoint pickup items may be a flat array (["3-Foo"]) or an object ({ item: ["3-Foo"] });
// read the first item string safely so a shape change can't crash the route render.
const getFirstPickupItemText = pickupAction => {
    if (Array.isArray(pickupAction)) {
        return String(pickupAction[0] || '');
    }
    if (pickupAction && Array.isArray(pickupAction.item)) {
        return String(pickupAction.item[0] || '');
    }
    return '';
};

const DispatchMap = props => {
    const {
        coordinates,
        selectedMarketLabelValue,
        routeClick,
        enrouteClickDriver,
        readyRoutes,
        deliveringRoutes,
        availableDrivers,
        enrouteDrivers,
        offlineDrivers,
        selectedMergingRoutes,
        openedMergingRoutes,
        showMergingRoutesMap,
        displayPolygon
    } = useGlobalContext();
    const selectedDriverStatuses = Array.isArray(props.statusFilter) ? props.statusFilter : [];
    const shouldRenderAvailableDrivers = selectedDriverStatuses.includes('available');
    const shouldRenderEnrouteDrivers = selectedDriverStatuses.includes('enroute');
    const shouldRenderOfflineDrivers = selectedDriverStatuses.includes('unavailable');

    const getDriverStatusFilterValue = driver => {
        if (normalizeDriverStatusFlag(driver && driver.is_online) && normalizeDriverStatusFlag(driver && driver.is_available)) {
            return 'available';
        }

        if (normalizeDriverStatusFlag(driver && driver.is_online) && !normalizeDriverStatusFlag(driver && driver.is_available)) {
            return 'enroute';
        }

        return 'unavailable';
    };
    const getDriverIdentifier = driver => {
        if (!driver) {
            return '';
        }

        const latestDriverAction = Array.isArray(driver.LastDriverActions)
            ? driver.LastDriverActions[0]
            : driver.LastDriverActions;

        return driver.driver_id ||
            (latestDriverAction && latestDriverAction.driver_id) ||
            driver.auth0_id ||
            (driver.User && driver.User.auth0_id) ||
            '';
    };

    const getDriverAuth0Identifier = driver => {
        if (!driver) {
            return '';
        }

        const latestDriverAction = Array.isArray(driver.LastDriverActions)
            ? driver.LastDriverActions[0]
            : driver.LastDriverActions;

        return driver.auth0_id ||
            (driver.User && driver.User.auth0_id) ||
            (driver.driver_blob && driver.driver_blob.auth0_id) ||
            (latestDriverAction && latestDriverAction.Driver && latestDriverAction.Driver.auth0_id) ||
            '';
    };

    const getDriverHydrationLookupKey = driver => {
        const auth0Identifier = getDriverAuth0Identifier(driver);

        if (auth0Identifier) {
            return `auth0:${auth0Identifier}`;
        }

        const identifier = getDriverIdentifier(driver);

        if (identifier) {
            return `id:${identifier}`;
        }

        return '';
    };

    const getDriverLookupIdentifiers = driver => {
        return [...new Set([
            getDriverIdentifier(driver),
            getDriverAuth0Identifier(driver),
        ].filter(identifier => typeof identifier === 'string' && identifier.trim()))];
    };

    const getDriverCoordinates = driver => {
        const normalizeCoordinates = (latValue, lngValue) => {
            const lat = typeof latValue === 'number' ? latValue : parseFloat(latValue);
            const lng = typeof lngValue === 'number' ? lngValue : parseFloat(lngValue);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return null;
            }

            if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                return null;
            }

            return { lat, lng };
        };

        const normalizeLocationBlob = locationBlob => {
            if (!locationBlob) {
                return null;
            }

            let normalizedBlob = locationBlob;

            if (typeof normalizedBlob === 'string') {
                try {
                    normalizedBlob = JSON.parse(normalizedBlob);
                } catch (error) {
                    return null;
                }
            }

            if (!normalizedBlob || typeof normalizedBlob !== 'object') {
                return null;
            }

            if ((normalizedBlob.lat === undefined && normalizedBlob.latitude === undefined) && Array.isArray(normalizedBlob.coordinates) && normalizedBlob.coordinates.length >= 2) {
                normalizedBlob = {
                    ...normalizedBlob,
                    lng: normalizedBlob.coordinates[0],
                    lat: normalizedBlob.coordinates[1]
                };
            }

            const nestedLocationBlob = normalizedBlob.location_blob || normalizedBlob.location;
            if ((normalizedBlob.lat === undefined && normalizedBlob.latitude === undefined) && nestedLocationBlob && typeof nestedLocationBlob === 'object') {
                normalizedBlob = {
                    ...normalizedBlob,
                    ...nestedLocationBlob
                };
            }

            return normalizeCoordinates(
                normalizedBlob.lat !== undefined ? normalizedBlob.lat : normalizedBlob.latitude,
                normalizedBlob.lng !== undefined ? normalizedBlob.lng : normalizedBlob.longitude
            );
        };

        if (!driver) {
            return null;
        }

        const driverActions = Array.isArray(driver.LastDriverActions)
            ? driver.LastDriverActions
            : (driver.LastDriverActions ? [driver.LastDriverActions] : []);
        const latestActionWithLocation = driverActions
            .slice()
            .sort((left, right) => new Date(right && right.timestamp ? right.timestamp : 0) - new Date(left && left.timestamp ? left.timestamp : 0))
            .find(action => action && action.location_blob);

        if (latestActionWithLocation && latestActionWithLocation.location_blob) {
            const liveCoordinates = normalizeLocationBlob(latestActionWithLocation.location_blob);

            if (liveCoordinates) {
                return liveCoordinates;
            }
        }

        const trackingBlob = driver && driver.tracking_blob ? driver.tracking_blob : null;

        if (trackingBlob) {
            const trackingCoordinates = normalizeLocationBlob(trackingBlob);

            if (trackingCoordinates) {
                return trackingCoordinates;
            }

            const trackingDetails = Array.isArray(trackingBlob.trackingDetails) ? trackingBlob.trackingDetails : [];
            const latestTrackingPoint = trackingDetails.length > 0 ? trackingDetails[trackingDetails.length - 1] : null;

            if (latestTrackingPoint) {
                const latestTrackingCoordinates = normalizeLocationBlob(latestTrackingPoint);

                if (latestTrackingCoordinates) {
                    return latestTrackingCoordinates;
                }
            }
        }

        const directLocationCandidates = [
            driver.location_blob,
            driver.driver_blob && driver.driver_blob.location_blob,
            driver.User && driver.User.location_blob,
            driver.LastDriverActions && driver.LastDriverActions.location_blob,
        ];

        for (const locationBlob of directLocationCandidates) {
            if (!locationBlob) {
                continue;
            }

            const directCoordinates = normalizeLocationBlob(locationBlob);

            if (directCoordinates) {
                return directCoordinates;
            }
        }

        const directFieldCoordinates = normalizeCoordinates(
            driver.lat !== undefined ? driver.lat : driver.latitude,
            driver.lng !== undefined ? driver.lng : driver.longitude
        );

        if (directFieldCoordinates) {
            return directFieldCoordinates;
        }

        const addresses = driver && driver.User && Array.isArray(driver.User.Addresses)
            ? driver.User.Addresses
            : [];

        const driverIdentifier = getDriverIdentifier(driver);

        if (driverIdentifier && geocodedDriverCoordinates[driverIdentifier]) {
            return geocodedDriverCoordinates[driverIdentifier];
        }

        for (const address of addresses) {
            const addressCoordinates = normalizeCoordinates(address && address.latitude, address && address.longitude);

            if (addressCoordinates) {
                return addressCoordinates;
            }
        }

        return null;
    };
    const getDriverRenderIdentity = driver => {
        const driverIdentifier = getDriverIdentifier(driver);

        if (driverIdentifier) {
            return driverIdentifier;
        }

        const coordinates = getDriverCoordinates(driver);

        if (coordinates) {
            return `${coordinates.lat}-${coordinates.lng}-${getDriverName(driver)}`;
        }

        return getDriverName(driver) || '';
    };

    const mergeDriverData = (baseDriver, enrichedDriver) => {
        if (!baseDriver) {
            return enrichedDriver;
        }

        if (!enrichedDriver) {
            return baseDriver;
        }

        const mergedUser = {
            ...(baseDriver && baseDriver.User ? baseDriver.User : {}),
            ...(enrichedDriver && enrichedDriver.User ? enrichedDriver.User : {}),
        };

        return {
            ...baseDriver,
            ...enrichedDriver,
            User: Object.keys(mergedUser).length > 0 ? mergedUser : null,
            Vehicles: Array.isArray(enrichedDriver.Vehicles) && enrichedDriver.Vehicles.length > 0 ? enrichedDriver.Vehicles : baseDriver.Vehicles,
            LastDriverActions: enrichedDriver.LastDriverActions || baseDriver.LastDriverActions,
            active_vehicle_details: enrichedDriver.active_vehicle_details || baseDriver.active_vehicle_details,
            vehicle_blob: enrichedDriver.vehicle_blob || baseDriver.vehicle_blob,
            image_url: enrichedDriver.image_url || baseDriver.image_url || '',
            tracking_blob: enrichedDriver.tracking_blob || baseDriver.tracking_blob || null,
        };
    };

    const getEffectiveDriver = driver => {
        if (!driver) {
            return driver;
        }

        const lookupIdentifiers = getDriverLookupIdentifiers(driver);
        const driverGroups = [
            Array.isArray(availableDrivers) ? availableDrivers : [],
            Array.isArray(enrouteDrivers) ? enrouteDrivers : [],
            Array.isArray(offlineDrivers) ? offlineDrivers : [],
        ];

        let mergedDriver = driver;

        if (lookupIdentifiers.length > 0) {
            for (const group of driverGroups) {
                const matchedDriver = group.find(candidate => {
                    const candidateIdentifiers = getDriverLookupIdentifiers(candidate);
                    return candidateIdentifiers.some(identifier => lookupIdentifiers.includes(identifier));
                });

                if (matchedDriver) {
                    mergedDriver = mergeDriverData(matchedDriver, mergedDriver);
                    // The live driver feed carries the current position (LastDriverActions/tracking_blob);
                    // let it win over the route assignment-time snapshot so the vehicle marker tracks the
                    // driver instead of freezing until a hard refresh.
                    const liveLastDriverActions = Array.isArray(matchedDriver.LastDriverActions)
                        ? matchedDriver.LastDriverActions
                        : (matchedDriver.LastDriverActions ? [matchedDriver.LastDriverActions] : []);
                    if (liveLastDriverActions.length > 0) {
                        mergedDriver = { ...mergedDriver, LastDriverActions: liveLastDriverActions };
                    }
                    if (matchedDriver.tracking_blob) {
                        mergedDriver = { ...mergedDriver, tracking_blob: matchedDriver.tracking_blob };
                    }
                    if (matchedDriver.location_blob) {
                        mergedDriver = { ...mergedDriver, location_blob: matchedDriver.location_blob };
                    }
                    break;
                }
            }
        }

        if (mergedDriver === driver) {
            const normalizePhone = value => String(value || '').replace(/\D/g, '');
            const normalizeName = value => String(value || '').trim().toLowerCase();
            const routePhone = normalizePhone(getDriverPhone(driver));
            const routeName = normalizeName(getDriverName(driver));

            const fallbackMatchByPhoneOrName = candidate => {
                const candidatePhone = normalizePhone(getDriverPhone(candidate));
                const candidateName = normalizeName(getDriverName(candidate));

                if (routePhone && candidatePhone && routePhone === candidatePhone) {
                    return true;
                }

                if (routeName && candidateName && routeName === candidateName) {
                    return true;
                }

                return false;
            };

            for (const group of driverGroups) {
                const matchedDriver = group.find(fallbackMatchByPhoneOrName);

                if (matchedDriver) {
                    mergedDriver = mergeDriverData(matchedDriver, mergedDriver);
                    const liveLastDriverActions = Array.isArray(matchedDriver.LastDriverActions)
                        ? matchedDriver.LastDriverActions
                        : (matchedDriver.LastDriverActions ? [matchedDriver.LastDriverActions] : []);
                    if (liveLastDriverActions.length > 0) {
                        mergedDriver = { ...mergedDriver, LastDriverActions: liveLastDriverActions };
                    }
                    if (matchedDriver.tracking_blob) {
                        mergedDriver = { ...mergedDriver, tracking_blob: matchedDriver.tracking_blob };
                    }
                    if (matchedDriver.location_blob) {
                        mergedDriver = { ...mergedDriver, location_blob: matchedDriver.location_blob };
                    }
                    break;
                }
            }
        }

        const driverIdentifier = getDriverHydrationLookupKey(mergedDriver);

        if (!driverIdentifier || !driverHydrationLookup[driverIdentifier]) {
            return mergedDriver;
        }

        return mergeDriverData(mergedDriver, driverHydrationLookup[driverIdentifier]);
    };

    const getScopeZoom = scopeType => {
        if (scopeType === 'country') {
            return 4;
        }

        if (scopeType === 'region') {
            return 6;
        }

        if (scopeType === 'market') {
            return 9;
        }

        return 8;
    };
    const MAX_ROUTE_FOCUS_ZOOM = 11;
    const ROUTE_FOCUS_PADDING = 120;
    const getRoutePoints = (route, includeCompleted = false) => {
        if (!route || !Array.isArray(route.path)) {
            return [];
        }

        return getVisibleRoutePath(route, includeCompleted)
            .map(stop => {
                const address = stop && stop.address ? stop.address : null;

                if (!address || typeof address.lat !== 'number' || typeof address.lng !== 'number') {
                    return null;
                }

                return { lat: address.lat, lng: address.lng };
            })
            .filter(Boolean);
    };

    const getRouteCenter = (route, includeCompleted = false) => {
        const points = getRoutePoints(route, includeCompleted);

        if (points.length === 0) {
            return null;
        }

        if (points.length === 1) {
            return points[0];
        }

        const bounds = points.reduce(
            (accumulator, point) => ({
                minLat: Math.min(accumulator.minLat, point.lat),
                maxLat: Math.max(accumulator.maxLat, point.lat),
                minLng: Math.min(accumulator.minLng, point.lng),
                maxLng: Math.max(accumulator.maxLng, point.lng)
            }),
            {
                minLat: points[0].lat,
                maxLat: points[0].lat,
                minLng: points[0].lng,
                maxLng: points[0].lng
            }
        );

        return {
            lat: (bounds.minLat + bounds.maxLat) / 2,
            lng: (bounds.minLng + bounds.maxLng) / 2
        };
    };
    const getWaypointMarkerKey = (routeKeyPrefix, route, stop, index, iconType) => {
        const routeIdentifier = route && (route.route_id || route.route_friendly_id || 'route');
        const stopIdentifier = stop && (stop.waypoint_id || stop.stop_id || stop.ship_id || stop.shipment_id || 'stop');
        const address = stop && stop.address ? stop.address : {};
        const coordinates = [address.lat, address.lng].filter(value => value !== undefined && value !== null).join('-') || 'no-coordinates';

        return `${routeKeyPrefix}-${routeIdentifier}-${stopIdentifier}-${iconType}-${coordinates}-${index}`;
    };
    const getDriverName = driver => {
        if (!driver) {
            return '';
        }

        if (driver.User) {
            return [driver.User.firstname || driver.User.first_name, driver.User.lastname || driver.User.last_name].filter(Boolean).join(' ');
        }

        if (driver.driver_blob) {
            return [driver.driver_blob.first_name, driver.driver_blob.last_name].filter(Boolean).join(' ');
        }

        return '';
    };

    const getDriverPhone = driver => {
        if (!driver) {
            return '';
        }

        return (driver.User && driver.User.phone) || (driver.driver_blob && driver.driver_blob.phone) || driver.phone || '';
    };

    const findMatchingDriverFromLists = driver => {
        if (!driver) {
            return null;
        }

        const driverGroups = [
            Array.isArray(availableDrivers) ? availableDrivers : [],
            Array.isArray(enrouteDrivers) ? enrouteDrivers : [],
            Array.isArray(offlineDrivers) ? offlineDrivers : [],
        ];

        const normalizePhone = value => String(value || '').replace(/\D/g, '');
        const normalizeName = value => String(value || '').trim().toLowerCase();
        const lookupIdentifiers = getDriverLookupIdentifiers(driver);
        const targetPhone = normalizePhone(getDriverPhone(driver));
        const targetName = normalizeName(getDriverName(driver));

        for (const group of driverGroups) {
            const matchedDriver = group.find(candidate => {
                const candidateIdentifiers = getDriverLookupIdentifiers(candidate);

                if (lookupIdentifiers.length > 0 && candidateIdentifiers.some(identifier => lookupIdentifiers.includes(identifier))) {
                    return true;
                }

                const candidatePhone = normalizePhone(getDriverPhone(candidate));
                if (targetPhone && candidatePhone && targetPhone === candidatePhone) {
                    return true;
                }

                const candidateName = normalizeName(getDriverName(candidate));
                if (targetName && candidateName && targetName === candidateName) {
                    return true;
                }

                return false;
            });

            if (matchedDriver) {
                return matchedDriver;
            }
        }

        return null;
    };

    const getRouteDriverIdentity = driver => {
        if (!driver) {
            return '';
        }

        return String(driver.driver_id || driver.auth0_id || getDriverPhone(driver) || getDriverName(driver)).trim();
    };

    const buildShipmentFallbackRouteDriver = shipment => {
        if (!(shipment && shipment.driver_blob)) {
            return null;
        }

        return {
            driver_id: shipment.driver_id || '',
            driver_blob: shipment.driver_blob,
            User: {
                first_name: shipment.driver_blob.first_name || '',
                last_name: shipment.driver_blob.last_name || '',
                firstname: shipment.driver_blob.first_name || '',
                lastname: shipment.driver_blob.last_name || '',
                phone: shipment.driver_blob.phone || '',
            },
            vehicle_blob: shipment.vehicle_blob || {},
            tracking_blob: shipment.tracking_blob || null,
            employment_type: shipment.employment_type || null,
            status: 'driver',
        };
    };

    const mergeRouteDriverRecords = (currentDriver, nextDriver) => {
        if (!currentDriver) {
            return nextDriver;
        }

        if (!nextDriver) {
            return currentDriver;
        }

        return {
            ...nextDriver,
            ...currentDriver,
            driver_id: currentDriver.driver_id || nextDriver.driver_id || '',
            driver_blob: currentDriver.driver_blob || nextDriver.driver_blob || null,
            User: currentDriver.User || nextDriver.User || null,
            vehicle_blob: currentDriver.vehicle_blob || nextDriver.vehicle_blob || {},
            tracking_blob: currentDriver.tracking_blob || nextDriver.tracking_blob || null,
            employment_type: currentDriver.employment_type || nextDriver.employment_type || null,
            status: currentDriver.status || nextDriver.status || 'driver',
        };
    };

    const getRouteDriversForDisplay = route => {
        const routeShipments = route && Array.isArray(route.Shipments) ? route.Shipments : [];
        const driverCandidates = [
            ...((route && Array.isArray(route.RouteDrivers) && route.RouteDrivers) || []),
            ...routeShipments.map(buildShipmentFallbackRouteDriver),
        ].filter(Boolean);

        return driverCandidates.reduce((drivers, candidateDriver) => {
            const driverIdentity = getRouteDriverIdentity(candidateDriver);

            if (!driverIdentity) {
                drivers.push(candidateDriver);
                return drivers;
            }

            const existingDriverIndex = drivers.findIndex(driver => getRouteDriverIdentity(driver) === driverIdentity);

            if (existingDriverIndex === -1) {
                drivers.push(candidateDriver);
                return drivers;
            }

            drivers[existingDriverIndex] = mergeRouteDriverRecords(drivers[existingDriverIndex], candidateDriver);
            return drivers;
        }, []);
    };

    const getActiveDriverVehicle = driver => {
        if (!driver) {
            return null;
        }

        const preferredVehicleId =
            driver.vehicle_id ||
            driver.active_vehicle ||
            (driver.User && driver.User.active_vehicle) ||
            (driver.LastDriverActions && driver.LastDriverActions.Driver && driver.LastDriverActions.Driver.vehicle_id) ||
            (driver.LastDriverActions && driver.LastDriverActions.Driver && driver.LastDriverActions.Driver.active_vehicle) ||
            null;

        const explicitlyAssignedVehicle = resolveDriverVehicle(driver, preferredVehicleId);

        if (explicitlyAssignedVehicle) {
            return explicitlyAssignedVehicle;
        }

        return resolveDriverActiveVehicle(driver);
    };

    const getDriverVehicleInfo = driver => {
        const vehicle = getActiveDriverVehicle(driver);

        if (!vehicle) {
            return 'No vehicle info';
        }

        return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') + (vehicle.color ? ` (${vehicle.color})` : '');
    };


    const getCachedDriverRenderUrl = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        const driverIdentifier = getDriverHydrationLookupKey(effectiveDriver);

        if (!driverIdentifier) {
            return '';
        }

        return driverRenderUrlLookup[driverIdentifier] || '';
    };

    const getDriverVehicleImageCandidates = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        return getDriverVehicleRenderCandidates(effectiveDriver);
    };

    const getDriverVehicleRawImageCandidates = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        const activeVehicle = getActiveDriverVehicle(effectiveDriver);
        const hydratedDriver = getEffectiveDriver(effectiveDriver);
        const hydratedDriverBlob = hydratedDriver && hydratedDriver.driver_blob ? hydratedDriver.driver_blob : null;
        const hydratedDriverUser = hydratedDriver && hydratedDriver.User ? hydratedDriver.User : null;
        const hydratedDriverUserVehicleBlob = hydratedDriverUser && hydratedDriverUser.vehicle_blob ? hydratedDriverUser.vehicle_blob : null;
        const vehicleBlob = hydratedDriver && hydratedDriver.vehicle_blob ? hydratedDriver.vehicle_blob : null;
        const activeVehicleDetails = hydratedDriver && hydratedDriver.active_vehicle_details ? hydratedDriver.active_vehicle_details : null;

        const rawCandidates = [
            activeVehicle && activeVehicle.render_image_url,
            activeVehicle && activeVehicle.renderImageUrl,
            activeVehicle && activeVehicle.image_url,
            activeVehicle && activeVehicle.imageUrl,
            activeVehicle && activeVehicle.vehicle_image_url,
            activeVehicle && activeVehicle.vehicleImageUrl,
            activeVehicleDetails && activeVehicleDetails.render_image_url,
            activeVehicleDetails && activeVehicleDetails.renderImageUrl,
            activeVehicleDetails && activeVehicleDetails.image_url,
            activeVehicleDetails && activeVehicleDetails.imageUrl,
            activeVehicleDetails && activeVehicleDetails.vehicle_image_url,
            activeVehicleDetails && activeVehicleDetails.vehicleImageUrl,
            vehicleBlob && vehicleBlob.render_image_url,
            vehicleBlob && vehicleBlob.renderImageUrl,
            vehicleBlob && vehicleBlob.image_url,
            vehicleBlob && vehicleBlob.imageUrl,
            vehicleBlob && vehicleBlob.vehicle_image_url,
            vehicleBlob && vehicleBlob.vehicleImageUrl,
            hydratedDriverBlob && hydratedDriverBlob.render_image_url,
            hydratedDriverBlob && hydratedDriverBlob.renderImageUrl,
            hydratedDriverBlob && hydratedDriverBlob.image_url,
            hydratedDriverBlob && hydratedDriverBlob.imageUrl,
            hydratedDriverBlob && hydratedDriverBlob.vehicle_image_url,
            hydratedDriverBlob && hydratedDriverBlob.vehicleImageUrl,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.render_image_url,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.renderImageUrl,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.image_url,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.imageUrl,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.vehicle_image_url,
            hydratedDriverUserVehicleBlob && hydratedDriverUserVehicleBlob.vehicleImageUrl,
            hydratedDriver && hydratedDriver.render_image_url,
            hydratedDriver && hydratedDriver.renderImageUrl,
            hydratedDriver && hydratedDriver.image_url,
            hydratedDriver && hydratedDriver.imageUrl,
            hydratedDriver && hydratedDriver.vehicle_image_url,
            hydratedDriver && hydratedDriver.vehicleImageUrl,
            hydratedDriverUser && hydratedDriverUser.render_image_url,
            hydratedDriverUser && hydratedDriverUser.renderImageUrl,
            hydratedDriverUser && hydratedDriverUser.image_url,
            hydratedDriverUser && hydratedDriverUser.imageUrl,
            hydratedDriverUser && hydratedDriverUser.vehicle_image_url,
            hydratedDriverUser && hydratedDriverUser.vehicleImageUrl,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.render_image_url,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.renderImageUrl,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.image_url,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.imageUrl,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.vehicle_image_url,
            hydratedDriver && hydratedDriver.Driver && hydratedDriver.Driver.vehicleImageUrl,
        ];

        return rawCandidates
            .map(candidate => normalizeAssetUrl(candidate))
            .filter(candidate => typeof candidate === 'string' && candidate.trim())
            .filter((candidate, index, allCandidates) => allCandidates.indexOf(candidate) === index);
    };

    const getDriverVehicleImageUrl = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        return getCachedDriverRenderUrl(effectiveDriver) || getDriverVehicleImageCandidates(effectiveDriver)[0] || getDriverVehicleRenderUrl(effectiveDriver);
    };

    const getDriverVehicleDisplayCandidates = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        const rawVehicleCandidates = getDriverVehicleRawImageCandidates(effectiveDriver);

        return [
            getCachedDriverRenderUrl(effectiveDriver),
            ...getDriverVehicleImageCandidates(effectiveDriver),
            getDriverVehicleRenderUrl(effectiveDriver),
        ]
            .filter(candidate => typeof candidate === 'string' && candidate.trim())
            .filter((candidate, index, allCandidates) => allCandidates.indexOf(candidate) === index);
    };

    const getDriverVehicleDisplayUrl = driver => {
        return getDriverVehicleDisplayCandidates(driver)[0] || '';
    };

    const getDriverPermit = driver => {
        if (!driver) {
            return '';
        }

        const permits = [];

        if (driver.can_deliver_alcohol) {
            permits.push('Alcohol');
        }

        if (Array.isArray(driver.restricted_area_credentials)) {
            driver.restricted_area_credentials.forEach(credential => {
                if (credential) {
                    permits.push(credential);
                }
            });
        }

        return permits.join(', ');
    };

    const normalizeDriverPhotoUrl = photoUrl => {
        if (typeof photoUrl !== 'string') {
            return '';
        }

        const trimmedPhotoUrl = photoUrl.trim();

        if (!trimmedPhotoUrl) {
            return '';
        }

        if (trimmedPhotoUrl.startsWith('data:') || trimmedPhotoUrl.startsWith('blob:')) {
            return trimmedPhotoUrl;
        }

        const isLocalStaticAsset = /^(?:\.?\/)?(?:static|assets)\//i.test(trimmedPhotoUrl);
        if (trimmedPhotoUrl.startsWith('/static/') || trimmedPhotoUrl.startsWith('/assets/') || isLocalStaticAsset) {
            const normalizedPath = trimmedPhotoUrl.startsWith('/')
                ? trimmedPhotoUrl
                : `/${trimmedPhotoUrl.replace(/^\.?\//, '')}`;

            if (typeof window !== 'undefined' && window.location && window.location.origin) {
                return `${window.location.origin}${normalizedPath}`;
            }

            return normalizedPath;
        }

        return normalizeAssetUrl(trimmedPhotoUrl);
    };

    const getDriverPhotoCandidates = driver => {
        if (!driver) {
            return [defaultDriverProfile];
        }

        const vehicleImageCandidates = new Set(
            [
                getDriverVehicleImageUrl(driver),
                ...getDriverVehicleImageCandidates(driver),
                ...getDriverVehicleRawImageCandidates(driver),
            ]
                .map(candidate => normalizeDriverPhotoUrl(candidate))
                .filter(candidate => typeof candidate === 'string' && candidate.trim())
        );

        const profileDocuments = [
            ...(Array.isArray(driver.UserDocuments) ? driver.UserDocuments : []),
            ...(Array.isArray(driver.User && driver.User.UserDocuments) ? driver.User.UserDocuments : []),
            ...(Array.isArray(driver.driver_blob && driver.driver_blob.UserDocuments) ? driver.driver_blob.UserDocuments : []),
        ]
            .filter(document => document && (document.doc_type === 'profile_photo' || document.doc_type === 'profile_picture'))
            .slice()
            .sort((left, right) => {
                const leftTimestamp = new Date(left && left.updated_at ? left.updated_at : 0).getTime();
                const rightTimestamp = new Date(right && right.updated_at ? right.updated_at : 0).getTime();
                return rightTimestamp - leftTimestamp;
            });

        const rawCandidates = [
            ...profileDocuments.map(document => document && (document.signed_url || document.url || document.path_id || '')),
            driver.profile_photo_url,
            driver.profile_picture_url,
            driver.profile_image_url,
            driver.photo,
            driver.photo_url,
            driver.avatar,
            driver.avatar_url,
            driver.image,
            driver.image_url,
            driver.driver_visibility && driver.driver_visibility.profile_photo_url,
            driver.driver_visibility && driver.driver_visibility.profile_picture_url,
            driver.User && driver.User.profile_photo_url,
            driver.User && driver.User.profile_picture_url,
            driver.User && driver.User.profile_image_url,
            driver.User && driver.User.photo,
            driver.User && driver.User.photo_url,
            driver.User && driver.User.avatar,
            driver.User && driver.User.avatar_url,
            driver.User && driver.User.image,
            driver.User && driver.User.image_url,
            driver.User && driver.User.driver_visibility && driver.User.driver_visibility.profile_photo_url,
            driver.User && driver.User.driver_visibility && driver.User.driver_visibility.profile_picture_url,
            driver.driver_blob && driver.driver_blob.profile_photo_url,
            driver.driver_blob && driver.driver_blob.profile_picture_url,
            driver.driver_blob && driver.driver_blob.profile_image_url,
            driver.driver_blob && driver.driver_blob.photo,
            driver.driver_blob && driver.driver_blob.avatar,
            defaultDriverProfile
        ];

        const normalizedCandidates = rawCandidates.reduce((candidates, candidate) => {
            const normalizedCandidate = normalizeDriverPhotoUrl(candidate);

            if (!normalizedCandidate) {
                return candidates;
            }

            if (vehicleImageCandidates.has(normalizedCandidate)) {
                return candidates;
            }

            if (!candidates.includes(normalizedCandidate)) {
                candidates.push(normalizedCandidate);
            }

            return candidates;
        }, []);

        const normalizedDefaultPhoto = normalizeDriverPhotoUrl(defaultDriverProfile);
        if (normalizedDefaultPhoto && !normalizedCandidates.includes(normalizedDefaultPhoto)) {
            normalizedCandidates.push(normalizedDefaultPhoto);
        }

        return normalizedCandidates;
    };

    const shouldHydrateDriver = driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        const driverIdentifier = getDriverHydrationLookupKey(effectiveDriver);

        if (!effectiveDriver || !driverIdentifier) {
            return false;
        }

        if (driverHydrationLookup[driverIdentifier] && driverHydrationLookup[driverIdentifier].__imageHydrated) {
            return false;
        }

        return true;
    };

    const fetchDispatchDriverRender = async driverIdentifiers => {
        const lookupIdentifiers = Array.isArray(driverIdentifiers)
            ? driverIdentifiers.filter(Boolean)
            : [driverIdentifiers].filter(Boolean);

        for (const driverId of lookupIdentifiers) {
            const directDriverRenderUrl = `${user_urls.usersHost}/driver/render/${encodeURIComponent(driverId)}`;
            const hostedDriverRenderUrl = typeof user_urls.toHostedUsersUrl === 'function'
                ? user_urls.toHostedUsersUrl(directDriverRenderUrl)
                : directDriverRenderUrl;
            const productionDriverRenderUrl = `${PRODUCTION_USERS_HOST}/driver/render/${encodeURIComponent(driverId)}`;
            const candidateUrls = [
                directDriverRenderUrl,
                hostedDriverRenderUrl,
                user_urls.hostedUsersHost ? `${user_urls.hostedUsersHost}/driver/render/${encodeURIComponent(driverId)}` : '',
                productionDriverRenderUrl,
            ].filter((url, index, collection) => typeof url === 'string' && url && collection.indexOf(url) === index);

            for (const url of candidateUrls) {
                try {
                    const response = await axios({
                        method: 'get',
                        timeout: 15000,
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url,
                    });

                    const rawPayload = response && response.data && response.data.payload ? response.data.payload : null;
                    const payload = rawPayload && rawPayload.driver ? rawPayload.driver : rawPayload;
                    const activeVehicle =
                        (payload && payload.active_vehicle_details) ||
                        (payload && payload.activeVehicleDetails) ||
                        (payload && payload.active_vehicle) ||
                        (payload && payload.activeVehicle) ||
                        (payload && payload.vehicle_blob) ||
                        (payload && payload.vehicleBlob) ||
                        null;
                    const renderUrl =
                        getVehicleRenderUrl(activeVehicle) ||
                        (activeVehicle && activeVehicle.render_image_url) ||
                        (activeVehicle && activeVehicle.renderImageUrl) ||
                        (payload && payload.render_image_url) ||
                        (payload && payload.renderImageUrl) ||
                        '';
                    const sharedAssetRenderUrl = renderUrl
                        ? ''
                        : await resolveSharedVehicleRenderUrl(getVehicleRenderSharedAssetIds(activeVehicle));
                    const resolvedRenderUrl = renderUrl || sharedAssetRenderUrl;
                    const imageUrl = resolvedRenderUrl || '';

                    if (resolvedRenderUrl || imageUrl || payload) {
                        return {
                            renderUrl: resolvedRenderUrl,
                            imageUrl,
                            derivedVehicleId: activeVehicle && activeVehicle.vehicle_id ? activeVehicle.vehicle_id : '',
                            hydratedDriver: payload,
                        };
                    }
                } catch (error) {
                    void error;
                }
            }
        }

        return { renderUrl: '', imageUrl: '', derivedVehicleId: '', hydratedDriver: null };
    };

    const hydrateDriver = async driver => {
        const driverIdentifier = getDriverHydrationLookupKey(driver);

        if (!driverIdentifier || driverHydrationInFlightRef.current[driverIdentifier]) {
            return;
        }

        driverHydrationInFlightRef.current[driverIdentifier] = true;

        try {
            let hydratedDriver = driver;
            const baseIdentifier = getDriverIdentifier(driver);

            const complianceIdentifier =
                (hydratedDriver && (hydratedDriver.auth0_id || hydratedDriver.driver_id || hydratedDriver.user_id)) ||
                (hydratedDriver && hydratedDriver.User && (hydratedDriver.User.auth0_id || hydratedDriver.User.user_id)) ||
                baseIdentifier;

            if (complianceIdentifier) {
                try {
                    const complianceResponse = await axios({
                        method: 'get',
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url: user_urls.driverCompliance(complianceIdentifier),
                    });

                    const compliancePayload = complianceResponse && complianceResponse.data ? complianceResponse.data.payload : null;
                    const complianceDriver = compliancePayload && compliancePayload.driver ? compliancePayload.driver : null;
                    const complianceVehicles = compliancePayload && Array.isArray(compliancePayload.vehicles) ? compliancePayload.vehicles : [];
                    const complianceOnboarding = compliancePayload && compliancePayload.compliance && compliancePayload.compliance.onboarding
                        ? compliancePayload.compliance.onboarding
                        : null;
                    const compliancePhotoUrl =
                        (complianceOnboarding && complianceOnboarding.driver_visibility && complianceOnboarding.driver_visibility.profile_photo_url) ||
                        (complianceDriver && complianceDriver.profile_photo_url) ||
                        '';
                    const activeVehicleId =
                        (complianceDriver && complianceDriver.active_vehicle) ||
                        (hydratedDriver && (hydratedDriver.active_vehicle || hydratedDriver.vehicle_id)) ||
                        '';
                    const activeComplianceVehicle =
                        complianceVehicles.find(vehicle => vehicle && vehicle.vehicle_id === activeVehicleId) ||
                        complianceVehicles[0] ||
                        null;
                    const preferredVehicleRenderUrl =
                        (activeComplianceVehicle && getVehicleRenderCandidates(activeComplianceVehicle)[0]) ||
                        (hydratedDriver && getDriverVehicleRenderUrl(hydratedDriver)) ||
                        (hydratedDriver && hydratedDriver.render_image_url) ||
                        '';

                    if (complianceDriver || complianceVehicles.length > 0 || compliancePhotoUrl) {
                        hydratedDriver = mergeDriverData(hydratedDriver, {
                            ...(complianceDriver || {}),
                            profile_photo_url: compliancePhotoUrl || (complianceDriver && complianceDriver.profile_photo_url) || '',
                            User: {
                                ...((hydratedDriver && hydratedDriver.User) || {}),
                                ...(complianceDriver || {}),
                                profile_photo_url: compliancePhotoUrl || (complianceDriver && complianceDriver.profile_photo_url) || ((hydratedDriver && hydratedDriver.User && hydratedDriver.User.profile_photo_url) || ''),
                            },
                            Vehicles: complianceVehicles.length > 0 ? complianceVehicles : (hydratedDriver && hydratedDriver.Vehicles),
                            active_vehicle: activeVehicleId || (hydratedDriver && hydratedDriver.active_vehicle) || '',
                            active_vehicle_details: activeComplianceVehicle || (hydratedDriver && hydratedDriver.active_vehicle_details) || null,
                            vehicle_blob: activeComplianceVehicle || (hydratedDriver && hydratedDriver.vehicle_blob) || null,
                            render_image_url: preferredVehicleRenderUrl,
                            image_url: preferredVehicleRenderUrl,
                        });
                    }
                } catch (complianceError) {
                    void complianceError;
                }
            }

            const hasStableHydratedRenderCandidate = getDriverVehicleRenderCandidates(hydratedDriver)
                .some(candidate => !isSignedAssetUrl(candidate));

            if (!hasStableHydratedRenderCandidate) {
                const fallbackRender = await fetchDispatchDriverRender(getDriverLookupIdentifiers(hydratedDriver));

                if (fallbackRender.renderUrl || fallbackRender.imageUrl || fallbackRender.hydratedDriver) {
                    // Preserve whichever vehicle blob actually carries a render image so a
                    // hydration response missing the AI render doesn't wipe one we already had.
                    const hydratedVehicleDetails = fallbackRender.hydratedDriver && fallbackRender.hydratedDriver.active_vehicle_details
                        ? fallbackRender.hydratedDriver.active_vehicle_details
                        : null;
                    const pickBlobWithRender = (...candidates) => {
                        const withRender = candidates.find(candidate => candidate && getVehicleRenderUrl(candidate));
                        return withRender || candidates.find(Boolean) || null;
                    };
                    const resolvedVehicleBlob = pickBlobWithRender(hydratedVehicleDetails, hydratedDriver.vehicle_blob);
                    const resolvedVehicleDetails = pickBlobWithRender(hydratedVehicleDetails, hydratedDriver.active_vehicle_details);
                    const recoveredRenderUrl = fallbackRender.renderUrl
                        || getVehicleRenderUrl(resolvedVehicleBlob)
                        || (hydratedDriver && hydratedDriver.render_image_url)
                        || '';

                    hydratedDriver = mergeDriverData(hydratedDriver, {
                        ...(fallbackRender.hydratedDriver || {}),
                        render_image_url: recoveredRenderUrl,
                        image_url: recoveredRenderUrl,
                        vehicle_image_url: (fallbackRender.hydratedDriver && fallbackRender.hydratedDriver.vehicle_image_url) || (hydratedDriver && hydratedDriver.vehicle_image_url) || '',
                        vehicle_blob: resolvedVehicleBlob,
                        active_vehicle_details: resolvedVehicleDetails,
                        active_vehicle: fallbackRender.derivedVehicleId || hydratedDriver.active_vehicle || '',
                    });
                }
            }

            setDriverHydrationLookup(currentLookup => ({
                ...currentLookup,
                [driverIdentifier]: {
                    ...hydratedDriver,
                    __imageHydrated: true,
                },
            }));
        } catch (error) {
            void error;
        } finally {
            delete driverHydrationInFlightRef.current[driverIdentifier];
        }
    };

    const initialCenter = {
        lat: coordinates && typeof coordinates.latitude === 'number' ? coordinates.latitude : 30.23119,
        lng: coordinates && typeof coordinates.longitude === 'number' ? coordinates.longitude : -93.21163
    };
    const defaultCenter = { lat: 30.23119, lng: -93.21163 };
    const DRIVER_FOCUS_ZOOM = 13;
    const [map, setMap] = useState('');
    const [maps, setMaps] = useState('');
    const [currentMapZoom, setCurrentMapZoom] = useState(8);
    const currentMapZoomRef = useRef(8);
    const [hoveredOfflineDriver, setHoveredOfflineDriver] = useState(null);
    const [offlineViewportTick, setOfflineViewportTick] = useState(0);
    const nativeOfflineMarkersRef = useRef(new Map());
    const nativeOfflineMarkerListenersRef = useRef(new Map());
    const hoveredOfflineDriverHideTimeoutRef = useRef(null);
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [mapCenter, setMapCenter] = useState(initialCenter);
    const mapCenterRef = useRef(initialCenter);
    const driverFocusViewportRef = useRef(null);
    const previousFocusedDriverIdRef = useRef('');
    const [visibleMapBounds, setVisibleMapBounds] = useState(null);
    const [driverHydrationLookup, setDriverHydrationLookup] = useState({});
    const [driverRenderUrlLookup, setDriverRenderUrlLookup] = useState({});
    const driverHydrationInFlightRef = useRef({});
    const [geocodedDriverCoordinates, setGeocodedDriverCoordinates] = useState({});
    const vehicleImageAssetLookupRef = useRef({});

    const [weatherTilePath, setWeatherTilePath] = useState(null);
    const geocodeRequestsRef = useRef(new Set());
    const weatherOverlayRef = useRef(null);
    const focusedRouteIdRef = useRef('');

    const [mapActive, setMapActive] = useState(false);

    const [hoveredDriver, setHoveredDriver] = useState(null);
    const resolveSharedVehicleRenderUrl = async sharedAssetIds => {
        const candidateSharedAssetIds = Array.isArray(sharedAssetIds)
            ? sharedAssetIds.filter(sharedAssetId => typeof sharedAssetId === 'string' && sharedAssetId.trim())
            : [];

        for (const sharedAssetId of candidateSharedAssetIds) {
            if (vehicleImageAssetLookupRef.current[sharedAssetId] !== undefined) {
                if (vehicleImageAssetLookupRef.current[sharedAssetId]) {
                    return vehicleImageAssetLookupRef.current[sharedAssetId];
                }

                continue;
            }

            let resolvedUrl = '';

            try {
                const response = await requestUsersHasuraWithFallback({
                    timeoutMs: 5000,
                    query: `query DispatchMapVehicleRenderDocument($assetId: String!) {
                        VehicleDocuments(
                            where: {
                                doc_type: {_eq: "vehicle_render"},
                                ocr_text: {_contains: {shared_asset_id: $assetId}}
                            },
                            order_by: {updated_at: desc},
                            limit: 1
                        ) {
                            signed_url
                        }
                    }`,
                    variables: { assetId: sharedAssetId },
                });

                const assets = response && response.data && response.data.data && Array.isArray(response.data.data.VehicleDocuments)
                    ? response.data.data.VehicleDocuments
                    : [];
                const signedUrl = assets[0] && typeof assets[0].signed_url === 'string'
                    ? assets[0].signed_url.trim()
                    : '';

                if (signedUrl) {
                    resolvedUrl = signedUrl;
                }
            } catch (error) {
                void error;
            }

            vehicleImageAssetLookupRef.current[sharedAssetId] = resolvedUrl;

            if (resolvedUrl) {
                return resolvedUrl;
            }
        }

        return '';
    };
    const hasActiveOrderExpansion = Boolean(props.hasActiveOrderExpansion);
    const selectedRouteId = (routeClick && routeClick.route_id) || '';
    const liveSelectedRoute = React.useMemo(() => {
        if (!selectedRouteId) {
            return null;
        }

        return [...readyRoutes, ...deliveringRoutes].find(route => route && route.route_id === selectedRouteId) || null;
    }, [deliveringRoutes, readyRoutes, selectedRouteId]);
    const selectedRoute = routeClick && isClosedRoute(routeClick)
        ? routeClick
        : liveSelectedRoute || routeClick || null;
    const activeRoute = selectedRoute;
    const routeDetailsVisible = hasActiveOrderExpansion && Boolean(activeRoute);
    const trackingRoute = routeDetailsVisible ? selectedRoute : null;
    const shouldRenderSelectedRouteTracking = Boolean(
        routeDetailsVisible &&
        trackingRoute &&
        (!routeClick || !routeClick.route_id || trackingRoute.route_id === routeClick.route_id)
    );
    const shouldRenderCompletedSelectedRoute = Boolean(routeDetailsVisible && activeRoute && CLOSED_ROUTE_STATUSES.has(activeRoute.status));
    const fallbackRoute = routeDetailsVisible && activeRoute && !shouldRenderCompletedSelectedRoute ? activeRoute : null;
    const trackingRouteDrivers = React.useMemo(() => getRouteDriversForDisplay(trackingRoute), [trackingRoute]);
    const shouldAllowDriverFocus = routeDetailsVisible || Boolean(props.focusedDriver);
    const activeFocusedDriver = shouldAllowDriverFocus
        ? getEffectiveDriver(
            props.focusedDriver || (trackingRoute && !CLOSED_ROUTE_STATUSES.has(trackingRoute.status) ? enrouteClickDriver || trackingRouteDrivers[0] || null : null)
        )
        : null;

    const hydrateDriverRenderOnly = async driver => {
        const effectiveDriver = getEffectiveDriver(driver);
        const driverIdentifier = getDriverHydrationLookupKey(effectiveDriver);

        if (!effectiveDriver || !driverIdentifier || driverHydrationInFlightRef.current[driverIdentifier]) {
            return;
        }

        driverHydrationInFlightRef.current[driverIdentifier] = true;

        try {
            const fallbackRender = await fetchDispatchDriverRender(getDriverLookupIdentifiers(effectiveDriver));

            // Prefer whichever vehicle blob actually carries a render image, so a hydration
            // response missing the AI render doesn't wipe a render we already had from the subscription.
            const hydratedVehicleDetails = fallbackRender.hydratedDriver && fallbackRender.hydratedDriver.active_vehicle_details
                ? fallbackRender.hydratedDriver.active_vehicle_details
                : null;
            const pickBlobWithRender = (...candidates) => {
                const withRender = candidates.find(candidate => candidate && getVehicleRenderUrl(candidate));
                return withRender || candidates.find(Boolean) || null;
            };
            const resolvedVehicleBlob = pickBlobWithRender(hydratedVehicleDetails, effectiveDriver.vehicle_blob);
            const resolvedVehicleDetails = pickBlobWithRender(hydratedVehicleDetails, effectiveDriver.active_vehicle_details);
            const recoveredRenderUrl = fallbackRender.renderUrl
                || getVehicleRenderUrl(resolvedVehicleBlob)
                || (effectiveDriver && effectiveDriver.render_image_url)
                || '';

            if (recoveredRenderUrl) {
                setDriverRenderUrlLookup(currentLookup => ({
                    ...currentLookup,
                    [driverIdentifier]: recoveredRenderUrl,
                }));
            } else {
                // Record attempted lookup to avoid refetching a known-missing render URL every refresh cycle.
                setDriverRenderUrlLookup(currentLookup => ({
                    ...currentLookup,
                    [driverIdentifier]: '',
                }));
            }

            if (fallbackRender.renderUrl || fallbackRender.imageUrl || fallbackRender.hydratedDriver) {
                const hydratedDriver = mergeDriverData(effectiveDriver, {
                    ...(fallbackRender.hydratedDriver || {}),
                    render_image_url: recoveredRenderUrl,
                    image_url: recoveredRenderUrl,
                    vehicle_image_url: (fallbackRender.hydratedDriver && fallbackRender.hydratedDriver.vehicle_image_url) || (effectiveDriver && effectiveDriver.vehicle_image_url) || '',
                    vehicle_blob: resolvedVehicleBlob,
                    active_vehicle_details: resolvedVehicleDetails,
                    active_vehicle: fallbackRender.derivedVehicleId || effectiveDriver.active_vehicle || '',
                });

                setDriverHydrationLookup(currentLookup => ({
                    ...currentLookup,
                    [driverIdentifier]: {
                        ...hydratedDriver,
                        __imageHydrated: true,
                    },
                }));
            }
        } catch (error) {
            void error;
            setDriverRenderUrlLookup(currentLookup => ({
                ...currentLookup,
                [driverIdentifier]: '',
            }));

            setDriverHydrationLookup(currentLookup => ({
                ...currentLookup,
                [driverIdentifier]: {
                    ...(getEffectiveDriver(driver) || {}),
                    __imageHydrated: true,
                },
            }));
        } finally {
            delete driverHydrationInFlightRef.current[driverIdentifier];
        }
    };

    useEffect(() => {
        if (activeFocusedDriver && shouldHydrateDriver(activeFocusedDriver)) {
            void hydrateDriver(activeFocusedDriver);
        }
    }, [activeFocusedDriver]);

    useEffect(() => {
        const routeClickDriver = getEffectiveDriver(enrouteClickDriver || trackingRouteDrivers[0] || null);

        if (routeClickDriver && shouldHydrateDriver(routeClickDriver)) {
            void hydrateDriver(routeClickDriver);
        }
    }, [enrouteClickDriver, trackingRouteDrivers]);

    const visibleDriversForImageHydration = React.useMemo(() => {
        const candidateDrivers = [
            ...(shouldRenderAvailableDrivers ? availableDrivers : []),
            ...(shouldRenderEnrouteDrivers ? enrouteDrivers : []),
            ...(Array.isArray(trackingRouteDrivers) ? trackingRouteDrivers : []),
            ...(activeFocusedDriver ? [activeFocusedDriver] : []),
        ]
            .map(driver => getEffectiveDriver(driver))
            .filter(driver => !visibleMapBounds || isCoordinateWithinVisibleBounds(getDriverCoordinates(driver), visibleMapBounds))
            .filter(Boolean);

        const dedupedDrivers = [];
        const seenDriverKeys = new Set();

        candidateDrivers.forEach(driver => {
            const lookupKey = getDriverHydrationLookupKey(driver);

            if (!lookupKey || seenDriverKeys.has(lookupKey)) {
                return;
            }

            seenDriverKeys.add(lookupKey);
            dedupedDrivers.push(driver);
        });

        return dedupedDrivers.slice(0, 30);
    }, [
        activeFocusedDriver,
        availableDrivers,
        enrouteDrivers,
        shouldRenderAvailableDrivers,
        shouldRenderEnrouteDrivers,
        trackingRouteDrivers,
        visibleMapBounds,
    ]);

    useEffect(() => {
        visibleDriversForImageHydration.forEach(driver => {
            const driverIdentifier = getDriverHydrationLookupKey(driver);

            if (!driverIdentifier) {
                return;
            }

            if (typeof driverRenderUrlLookup[driverIdentifier] === 'undefined' || shouldHydrateDriver(driver)) {
                void hydrateDriverRenderOnly(driver);
            }
        });
    }, [visibleDriversForImageHydration, driverRenderUrlLookup]);
    const visibleMergingRoutes = React.useMemo(() => {
        const routeMap = new Map();

        [...(Array.isArray(openedMergingRoutes) ? openedMergingRoutes : []), ...(Array.isArray(selectedMergingRoutes) ? selectedMergingRoutes : [])].forEach(route => {
            if (route && route.route_id && !routeMap.has(route.route_id)) {
                routeMap.set(route.route_id, route);
            }
        });

        return Array.from(routeMap.values());
    }, [openedMergingRoutes, selectedMergingRoutes]);

    const getDriverAddressQuery = driver => {
        const addresses = driver && driver.User && Array.isArray(driver.User.Addresses)
            ? driver.User.Addresses
            : [];

        const preferredAddress = addresses.find(address => address && address.is_default) || addresses[0];

        if (!preferredAddress) {
            return '';
        }

        return [
            preferredAddress.address_1,
            preferredAddress.city,
            preferredAddress.state,
            preferredAddress.zip,
            preferredAddress.country,
        ].filter(Boolean).join(', ');
    };

    useEffect(() => {
        if (!maps || typeof maps.Geocoder !== 'function') {
            return;
        }

        const geocoder = new maps.Geocoder();
        const focusedOfflineDriver = hoveredOfflineDriver && hoveredOfflineDriver.driver ? hoveredOfflineDriver.driver : null;
        const driversToResolve = [
            ...(Array.isArray(availableDrivers) ? availableDrivers : []),
            ...(Array.isArray(enrouteDrivers) ? enrouteDrivers : []),
            ...(activeFocusedDriver ? [activeFocusedDriver] : []),
            ...(hoveredDriver ? [hoveredDriver] : []),
            ...(focusedOfflineDriver ? [focusedOfflineDriver] : []),
        ];

        driversToResolve.forEach(driver => {
            const driverIdentifier = getDriverIdentifier(driver);
            if (!driverIdentifier) {
                return;
            }

            if (getDriverCoordinates({ ...driver, User: driver && driver.User ? driver.User : {} })) {
                return;
            }

            if (geocodedDriverCoordinates[driverIdentifier] || geocodeRequestsRef.current.has(driverIdentifier)) {
                return;
            }

            const addressQuery = getDriverAddressQuery(driver);
            if (!addressQuery) {
                return;
            }

            geocodeRequestsRef.current.add(driverIdentifier);
            geocoder.geocode({ address: addressQuery }, (results, status) => {
                geocodeRequestsRef.current.delete(driverIdentifier);

                if (status !== 'OK' || !Array.isArray(results) || results.length === 0 || !results[0].geometry || !results[0].geometry.location) {
                    return;
                }

                const location = results[0].geometry.location;
                const nextCoordinates = {
                    lat: typeof location.lat === 'function' ? location.lat() : location.lat,
                    lng: typeof location.lng === 'function' ? location.lng() : location.lng,
                };

                if (!Number.isFinite(nextCoordinates.lat) || !Number.isFinite(nextCoordinates.lng)) {
                    return;
                }

                setGeocodedDriverCoordinates(currentCoordinates => {
                    if (currentCoordinates[driverIdentifier]) {
                        return currentCoordinates;
                    }

                    return {
                        ...currentCoordinates,
                        [driverIdentifier]: nextCoordinates,
                    };
                });
            });
        });
    }, [availableDrivers, enrouteDrivers, activeFocusedDriver, geocodedDriverCoordinates, hoveredDriver, hoveredOfflineDriver, maps]);

    useEffect(() => {
        if (!props.weatherLayer) {
            setWeatherTilePath(null);
            return undefined;
        }

        let isCancelled = false;

        const loadWeatherTilePath = async () => {
            try {
                const response = await fetch(RAIN_VIEWER_MANIFEST_URL);

                if (!response.ok) {
                    throw new Error(`weather manifest ${response.status}`);
                }

                const manifest = await response.json();
                const latestTilePath = getLatestWeatherTilePath(manifest);

                if (!isCancelled && latestTilePath) {
                    setWeatherTilePath(latestTilePath);
                }
            } catch (error) {
                if (!isCancelled) {
                    setWeatherTilePath(null);
                }
            }
        };

        loadWeatherTilePath();
        const refreshTimer = window.setInterval(loadWeatherTilePath, WEATHER_OVERLAY_REFRESH_MS);

        return () => {
            isCancelled = true;
            window.clearInterval(refreshTimer);
        };
    }, [props.weatherLayer]);

    useEffect(() => {
        if (!map || !maps || !map.overlayMapTypes || typeof map.overlayMapTypes.getLength !== 'function') {
            return undefined;
        }

        const removeWeatherOverlay = () => {
            if (!weatherOverlayRef.current) {
                return;
            }

            for (let index = map.overlayMapTypes.getLength() - 1; index >= 0; index -= 1) {
                if (map.overlayMapTypes.getAt(index) === weatherOverlayRef.current) {
                    map.overlayMapTypes.removeAt(index);
                }
            }

            weatherOverlayRef.current = null;
        };

        removeWeatherOverlay();

        if (!props.weatherLayer || !weatherTilePath || typeof maps.ImageMapType !== 'function' || typeof maps.Size !== 'function') {
            return removeWeatherOverlay;
        }

        const weatherOverlay = new maps.ImageMapType({
            getTileUrl: (coord, zoom) => {
                const scale = 1 << zoom;

                if (coord.y < 0 || coord.y >= scale) {
                    return null;
                }

                const normalizedX = ((coord.x % scale) + scale) % scale;

                return `${RAIN_VIEWER_TILE_HOST}${weatherTilePath}/${WEATHER_TILE_SIZE}/${zoom}/${normalizedX}/${coord.y}/${WEATHER_COLOR_SCHEME}/${WEATHER_SMOOTHING}_${WEATHER_SNOW}.png`;
            },
            tileSize: new maps.Size(WEATHER_TILE_SIZE, WEATHER_TILE_SIZE),
            name: 'Weather Radar',
            opacity: 0.6,
            maxZoom: 20,
        });

        weatherOverlayRef.current = weatherOverlay;
        map.overlayMapTypes.insertAt(0, weatherOverlay);

        return removeWeatherOverlay;
    }, [map, maps, props.weatherLayer, weatherTilePath]);

    const MapOverlay = ({ children }) => <div style={{ display: 'none' }}>{children}</div>;
    const focusedDriverId = getDriverIdentifier(activeFocusedDriver);
    const focusedDriverCoordinates = getDriverCoordinates(activeFocusedDriver);
    const effectiveRouteClickDriver = getEffectiveDriver(enrouteClickDriver || trackingRouteDrivers[0] || null);
    const focusedDriverVisible =
        focusedDriverId !== '' &&
        [availableDrivers, enrouteDrivers, offlineDrivers].some(driverGroup =>
            driverGroup.some(driver => getDriverIdentifier(driver) === focusedDriverId && Boolean(getDriverCoordinates(driver)))
        );
    const routeClickDriverCoordinates = React.useMemo(() => {
        if (isClosedRoute(trackingRoute)) {
            return null;
        }

        const directCoordinates = getDriverCoordinates(effectiveRouteClickDriver);
        const directTimestamp = getDriverLiveLocationTimestamp(effectiveRouteClickDriver);

        const locationUpdates = filterRouteLocationUpdates(
            trackingRoute,
            trackingRoute && Array.isArray(trackingRoute.LocationUpdates) ? trackingRoute.LocationUpdates : []
        );
        const latestLocationUpdate = locationUpdates
            .slice()
            .sort((left, right) => getComparableTimestamp(right && right.timestamp) - getComparableTimestamp(left && left.timestamp))[0] || null;
        const locationUpdateCoordinates = latestLocationUpdate
            ? getDriverCoordinates({
                location_blob: {
                    latitude: latestLocationUpdate.latitude,
                    longitude: latestLocationUpdate.longitude
                }
            })
            : null;
        const locationUpdateTimestamp = latestLocationUpdate ? getComparableTimestamp(latestLocationUpdate.timestamp) : Number.NaN;

        // Authenticated app coordinates (LastDriverActions) stay authoritative while they're a live
        // signal. For a link-only driver the app isn't posting (no authenticated timestamp) or the
        // breadcrumb is strictly newer, so follow the web-link breadcrumb head instead of freezing
        // the vehicle on the stale assignment-time snapshot.
        const breadcrumbIsFresher = Boolean(locationUpdateCoordinates) && (
            !Number.isFinite(directTimestamp) ||
            (Number.isFinite(locationUpdateTimestamp) && locationUpdateTimestamp > directTimestamp)
        );

        if (breadcrumbIsFresher) {
            return locationUpdateCoordinates;
        }

        if (directCoordinates) {
            return directCoordinates;
        }

        if (locationUpdateCoordinates) {
            return locationUpdateCoordinates;
        }

        const trackingDetails = filterRouteTrackingDetails(
            trackingRoute,
            trackingRouteDrivers[0] && trackingRouteDrivers[0].tracking_blob && Array.isArray(trackingRouteDrivers[0].tracking_blob.trackingDetails)
                ? trackingRouteDrivers[0].tracking_blob.trackingDetails
                : []
        );
        const latestTrackingPoint = trackingDetails.length > 0 ? trackingDetails[trackingDetails.length - 1] : null;

        if (latestTrackingPoint) {
            const trackingPointCoordinates = getDriverCoordinates({
                location_blob: {
                    latitude: latestTrackingPoint.latitude,
                    longitude: latestTrackingPoint.longitude,
                    lat: latestTrackingPoint.lat,
                    lng: latestTrackingPoint.lng
                }
            });

            if (trackingPointCoordinates) {
                return trackingPointCoordinates;
            }
        }

        return null;
    }, [effectiveRouteClickDriver, getDriverCoordinates, trackingRoute, trackingRouteDrivers]);
    const matchedRouteClickDriver = findMatchingDriverFromLists(effectiveRouteClickDriver);
    const routeClickDriverMarker = matchedRouteClickDriver
        ? mergeDriverData(matchedRouteClickDriver, effectiveRouteClickDriver)
        : (effectiveRouteClickDriver || {
        User: {
            firstname: 'Driver',
            lastname: ''
        }
    });
    const routeClickDriverName = getDriverName(effectiveRouteClickDriver);
    const shouldRenderLiveRouteDriverMarker = Boolean(routeDetailsVisible && trackingRoute && !isClosedRoute(trackingRoute) && routeClickDriverCoordinates);
    const focusedDriverTrackingDetails = React.useMemo(() => {
        if (!activeFocusedDriver || !activeFocusedDriver.tracking_blob || !Array.isArray(activeFocusedDriver.tracking_blob.trackingDetails)) {
            return [];
        }

        return activeFocusedDriver.tracking_blob.trackingDetails.filter(track => {
            const latitude = typeof track.latitude === 'number' ? track.latitude : parseFloat(track.latitude);
            const longitude = typeof track.longitude === 'number' ? track.longitude : parseFloat(track.longitude);

            return Number.isFinite(latitude) && Number.isFinite(longitude);
        });
    }, [activeFocusedDriver]);
    const shouldRenderFocusedDriverTracking = Boolean(
        routeDetailsVisible &&
        !shouldRenderSelectedRouteTracking &&
        activeFocusedDriver &&
        focusedDriverTrackingDetails.length > 0
    );

    const handleDriverMarkerHover = driver => {
        setHoveredDriver(driver);
    };

    const handleDriverMarkerLeave = () => {
        setHoveredDriver(null);
    };

    const isSameDriver = (leftDriver, rightDriver) => {
        const leftIdentifier = getDriverIdentifier(leftDriver);
        const rightIdentifier = getDriverIdentifier(rightDriver);

        if (!leftIdentifier || !rightIdentifier) {
            return leftDriver === rightDriver;
        }

        return leftIdentifier === rightIdentifier;
    };

    const updateMapCenter = nextCenter => {
        if (!nextCenter || !Number.isFinite(nextCenter.lat) || !Number.isFinite(nextCenter.lng)) {
            return false;
        }

        if (areCoordinatesEqual(mapCenterRef.current, nextCenter)) {
            return false;
        }

        mapCenterRef.current = nextCenter;
        setMapCenter(nextCenter);
        return true;
    };

    const syncVisibleMapBounds = React.useCallback(rawBounds => {
        const nextBounds = padVisibleMapBounds(normalizeVisibleMapBounds(rawBounds));

        if (!nextBounds) {
            return;
        }

        setVisibleMapBounds(currentBounds => {
            if (areVisibleMapBoundsEqual(currentBounds, nextBounds)) {
                return currentBounds;
            }

            return nextBounds;
        });
    }, []);

    useEffect(() => {
        if (typeof props.onVisibleBoundsChange === 'function') {
            props.onVisibleBoundsChange(
                visibleMapBounds
                    ? {
                        ...visibleMapBounds,
                        zoom: currentMapZoom,
                        includeOffline: shouldRenderOfflineDrivers,
                    }
                    : {
                        zoom: currentMapZoom,
                        includeOffline: shouldRenderOfflineDrivers,
                    }
            );
        }
    }, [currentMapZoom, props.onVisibleBoundsChange, shouldRenderOfflineDrivers, visibleMapBounds]);

    const shouldExpandOfflineDrivers = React.useMemo(
        () => shouldExpandOfflineDriversForBounds(visibleMapBounds),
        [visibleMapBounds]
    );

    useEffect(() => {
        const t = setTimeout(() => {
            setMapActive(true);
        }, 50);

        return () => {
            window.clearTimeout(t);
        };
    }, []);

    useEffect(() => {
        if (!routeDetailsVisible || !activeRoute) {
            focusedRouteIdRef.current = '';
            return;
        }

        // Only auto-fit/center when the selected route (or its completed/active view)
        // CHANGES. Re-fitting on every poll refresh or driver-position update fights the
        // user's manual zoom/pan on a live delivering route (symptom: cannot zoom in).
        const routeIdentity = activeRoute.route_id || activeRoute.route_friendly_id || selectedRouteId || '';
        const focusKey = routeIdentity ? `${routeIdentity}:${shouldRenderCompletedSelectedRoute ? 'completed' : 'active'}` : '';

        if (focusKey && focusedRouteIdRef.current === focusKey) {
            return;
        }

        const routePoints = getRoutePoints(activeRoute, shouldRenderCompletedSelectedRoute);

        if (routePoints.length === 0) {
            return;
        }

        const nextCenter = getRouteCenter(activeRoute, shouldRenderCompletedSelectedRoute);

        if (!nextCenter) {
            return;
        }

        focusedRouteIdRef.current = focusKey;

        updateMapCenter(nextCenter);

        if (map && maps && typeof maps.LatLngBounds === 'function' && typeof map.fitBounds === 'function' && routePoints.length > 1) {
            const bounds = new maps.LatLngBounds();

            routePoints.forEach(point => {
                bounds.extend(point);
            });

            // Include driver's current position so the marker is always in view
            if (!shouldRenderCompletedSelectedRoute && routeClickDriverCoordinates) {
                bounds.extend(routeClickDriverCoordinates);
            }

            map.fitBounds(bounds, ROUTE_FOCUS_PADDING);

            if (maps.event && typeof maps.event.addListenerOnce === 'function' && typeof map.getZoom === 'function' && typeof map.setZoom === 'function') {
                maps.event.addListenerOnce(map, 'idle', () => {
                    const fittedZoom = map.getZoom();

                    if (typeof fittedZoom === 'number' && fittedZoom > MAX_ROUTE_FOCUS_ZOOM) {
                        map.setZoom(MAX_ROUTE_FOCUS_ZOOM);
                    }
                });
            }

            return;
        }

        if (map && typeof map.panTo === 'function') {
            map.panTo(nextCenter);
        }
    }, [activeRoute, map, maps, routeClickDriverCoordinates, routeDetailsVisible, selectedRouteId, shouldRenderCompletedSelectedRoute]);

    useEffect(() => {
        if (activeRoute || !showMergingRoutesMap || visibleMergingRoutes.length === 0) {
            return;
        }

        const routePoints = visibleMergingRoutes.flatMap(route => getRoutePoints(route));

        if (routePoints.length === 0) {
            return;
        }

        const boundsSummary = routePoints.reduce(
            (accumulator, point) => ({
                minLat: Math.min(accumulator.minLat, point.lat),
                maxLat: Math.max(accumulator.maxLat, point.lat),
                minLng: Math.min(accumulator.minLng, point.lng),
                maxLng: Math.max(accumulator.maxLng, point.lng)
            }),
            {
                minLat: routePoints[0].lat,
                maxLat: routePoints[0].lat,
                minLng: routePoints[0].lng,
                maxLng: routePoints[0].lng
            }
        );

        const nextCenter = {
            lat: (boundsSummary.minLat + boundsSummary.maxLat) / 2,
            lng: (boundsSummary.minLng + boundsSummary.maxLng) / 2,
        };

        updateMapCenter(nextCenter);

        if (map && maps && typeof maps.LatLngBounds === 'function' && typeof map.fitBounds === 'function' && routePoints.length > 1) {
            const bounds = new maps.LatLngBounds();
            routePoints.forEach(point => bounds.extend(point));
            map.fitBounds(bounds, ROUTE_FOCUS_PADDING);

            if (maps.event && typeof maps.event.addListenerOnce === 'function' && typeof map.getZoom === 'function' && typeof map.setZoom === 'function') {
                maps.event.addListenerOnce(map, 'idle', () => {
                    const fittedZoom = map.getZoom();

                    if (typeof fittedZoom === 'number' && fittedZoom > MAX_ROUTE_FOCUS_ZOOM) {
                        map.setZoom(MAX_ROUTE_FOCUS_ZOOM);
                    }
                });
            }

            return;
        }

        if (map && typeof map.panTo === 'function') {
            map.panTo(nextCenter);
        }
    }, [activeRoute, map, maps, showMergingRoutesMap, visibleMergingRoutes]);

    useEffect(() => {
        if (activeRoute) {
            return;
        }

        if (showMergingRoutesMap && visibleMergingRoutes.length > 0) {
            return;
        }

        const currentFocusedDriverId = getDriverIdentifier(activeFocusedDriver);
        const previousFocusedDriverId = previousFocusedDriverIdRef.current;

        if (!activeFocusedDriver) {
            const savedViewport = driverFocusViewportRef.current;

            previousFocusedDriverIdRef.current = '';

            if (savedViewport && savedViewport.center) {
                updateMapCenter(savedViewport.center);

                if (map && typeof map.panTo === 'function') {
                    map.panTo(savedViewport.center);
                }

                if (map && typeof map.setZoom === 'function' && Number.isFinite(savedViewport.zoom)) {
                    map.setZoom(savedViewport.zoom);
                }

                driverFocusViewportRef.current = null;
            }

            // no saved viewport — leave the map exactly where the user left it
            return;
        }

        if (!previousFocusedDriverId || previousFocusedDriverId !== currentFocusedDriverId) {
            const liveCenter = map && typeof map.getCenter === 'function'
                ? map.getCenter()
                : null;
            const liveZoom = map && typeof map.getZoom === 'function'
                ? map.getZoom()
                : null;
            const snapshotCenter = liveCenter && typeof liveCenter.lat === 'function' && typeof liveCenter.lng === 'function'
                ? { lat: liveCenter.lat(), lng: liveCenter.lng() }
                : mapCenterRef.current;

            if (snapshotCenter && Number.isFinite(snapshotCenter.lat) && Number.isFinite(snapshotCenter.lng)) {
                driverFocusViewportRef.current = {
                    center: snapshotCenter,
                    zoom: Number.isFinite(liveZoom) ? liveZoom : currentMapZoomRef.current,
                };
            }
        }

        previousFocusedDriverIdRef.current = currentFocusedDriverId;

        const nextCenter = getDriverCoordinates(activeFocusedDriver);

        if (!nextCenter) {
            return;
        }

        updateMapCenter(nextCenter);

        if (map && typeof map.panTo === 'function') {
            map.panTo(nextCenter);
        }

        if (map && typeof map.setZoom === 'function') {
            map.setZoom(DRIVER_FOCUS_ZOOM);
        }
    }, [activeFocusedDriver, activeRoute, geocodedDriverCoordinates, map, showMergingRoutesMap, visibleMergingRoutes]); // eslint-disable-line react-hooks/exhaustive-deps

    const drawPolylines = (map, maps, routes, includeCompleted = false) => {
        const renderableRoutes = (Array.isArray(routes) ? routes : [])
            .map(route => getRenderableRoute(route, includeCompleted))
            .filter(Boolean);

        if (renderableRoutes.length === 0) {
            return null;
        }

        return (
            <MapOverlay>
                <Polyline map={map} maps={maps} routes={renderableRoutes} />
            </MapOverlay>
        );
    };

    const drawPolygons = (map, maps, routes, includeCompleted = false) => {
        const renderableRoutes = (Array.isArray(routes) ? routes : [])
            .map(route => getRenderableRoute(route, includeCompleted))
            .filter(route => route && hasRenderableBounds(route));

        if (renderableRoutes.length === 0) {
            return null;
        }

        return (
            <MapOverlay>
                <Polygon map={map} maps={maps} routes={renderableRoutes} />
            </MapOverlay>
        );
    };

    const handleApiLoaded = (map, maps) => {
        setMap(map);
        setMaps(maps);
        if (map && typeof map.getZoom === 'function') {
            const nextZoom = map.getZoom();

            if (typeof nextZoom === 'number' && Number.isFinite(nextZoom)) {
                setCurrentMapZoom(nextZoom);
            }
        }
        if (map && typeof map.getBounds === 'function') {
            const bounds = map.getBounds();

            if (bounds && typeof bounds.getNorthEast === 'function' && typeof bounds.getSouthWest === 'function') {
                const northEast = bounds.getNorthEast();
                const southWest = bounds.getSouthWest();

                syncVisibleMapBounds({
                    ne: { lat: northEast.lat(), lng: northEast.lng() },
                    sw: { lat: southWest.lat(), lng: southWest.lng() },
                });
            }
        }
        setMapsLoaded(true);
    };

    useEffect(() => {
        if (!map || !maps || !maps.event || typeof maps.event.addListener !== 'function' || typeof map.getZoom !== 'function') {
            return undefined;
        }

        const zoomListener = maps.event.addListener(map, 'zoom_changed', () => {
            const nextZoom = map.getZoom();

            if (typeof nextZoom === 'number' && Number.isFinite(nextZoom)) {
                currentMapZoomRef.current = nextZoom;
                setCurrentMapZoom(nextZoom);
                const nextBounds = typeof map.getBounds === 'function' ? map.getBounds() : null;
                const nextBoundsJSON = nextBounds && typeof nextBounds.toJSON === 'function' ? nextBounds.toJSON() : null;

                if (nextBoundsJSON) {
                    syncVisibleMapBounds(nextBoundsJSON);
                }

                if (!shouldExpandOfflineDriversForBounds(nextBoundsJSON)) {
                    setHoveredOfflineDriver(null);
                }
            }
        });

        return () => {
            if (maps.event && typeof maps.event.removeListener === 'function' && zoomListener) {
                maps.event.removeListener(zoomListener);
            }
        };
    }, [map, maps, syncVisibleMapBounds]);

    useEffect(() => {
        if (!map || !maps || !maps.event || typeof maps.event.addListener !== 'function') {
            return undefined;
        }

        const idleListener = maps.event.addListener(map, 'idle', () => {
            const nextCenter = typeof map.getCenter === 'function' ? map.getCenter() : null;
            const nextBounds = typeof map.getBounds === 'function' ? map.getBounds() : null;
            const nextBoundsJSON = nextBounds && typeof nextBounds.toJSON === 'function' ? nextBounds.toJSON() : null;

            if (nextCenter && typeof nextCenter.lat === 'function' && typeof nextCenter.lng === 'function') {
                updateMapCenter({ lat: nextCenter.lat(), lng: nextCenter.lng() });
            }

            if (nextBoundsJSON) {
                syncVisibleMapBounds(nextBoundsJSON);
            }

            setOfflineViewportTick(tick => tick + 1);
        });

        return () => {
            if (maps.event && typeof maps.event.removeListener === 'function' && idleListener) {
                maps.event.removeListener(idleListener);
            }
        };
    }, [map, maps, syncVisibleMapBounds]);

    useEffect(() => {
        const clearNativeOfflineMarkers = () => {
            nativeOfflineMarkersRef.current.forEach(marker => {
                marker.setMap(null);
            });
            nativeOfflineMarkersRef.current.clear();

            nativeOfflineMarkerListenersRef.current.forEach(listenerSet => {
                listenerSet.forEach(listener => {
                    if (maps && maps.event && typeof maps.event.removeListener === 'function' && listener) {
                        maps.event.removeListener(listener);
                    }
                });
            });
            nativeOfflineMarkerListenersRef.current.clear();
        };

        const syncNativeOfflineMarkerListeners = (markerKey, nextListeners) => {
            const previousListeners = nativeOfflineMarkerListenersRef.current.get(markerKey) || [];

            previousListeners.forEach(listener => {
                if (maps && maps.event && typeof maps.event.removeListener === 'function' && listener) {
                    maps.event.removeListener(listener);
                }
            });

            nativeOfflineMarkerListenersRef.current.set(markerKey, nextListeners);
        };

        const buildNativeOfflineMarkerIcon = markerItem => {
            if (markerItem.kind === 'cluster') {
                return {
                    path: maps.SymbolPath.CIRCLE,
                    scale: getOfflineClusterMarkerScale(markerItem.count),
                    fillColor: '#374151',
                    fillOpacity: 0.94,
                    strokeColor: '#ffffff',
                    strokeWeight: 1.5,
                };
            }

            return {
                path: maps.SymbolPath.CIRCLE,
                scale: 5,
                fillColor: '#6b7280',
                fillOpacity: 0.95,
                strokeColor: '#ffffff',
                strokeWeight: 1.25,
            };
        };

        const buildNativeOfflineMarkerLabel = markerItem => {
            if (markerItem.kind !== 'cluster') {
                return null;
            }

            return {
                text: String(markerItem.count),
                color: '#ffffff',
                fontSize: markerItem.count >= 100 ? '11px' : '10px',
                fontWeight: '700',
            };
        };

        const buildOfflineMarkerRenderItems = offlineDriverEntries => {
            if (shouldRenderIndividualOfflineDrivers) {
                return offlineDriverEntries.map(entry => ({
                    kind: 'driver',
                    key: `driver:${entry.driverIdentifier}`,
                    driver: entry.driver,
                    position: entry.coordinates,
                    title: getDriverName(entry.driver) || 'Offline driver',
                }));
            }

            const clusterCellSize = getOfflineDriverClusterCellSize(currentMapZoom);
            const clusterLookup = new Map();

            offlineDriverEntries.forEach(entry => {
                const latBucket = Math.floor(entry.coordinates.lat / clusterCellSize);
                const lngBucket = Math.floor(entry.coordinates.lng / clusterCellSize);
                const clusterKey = `${latBucket}:${lngBucket}`;
                const existingCluster = clusterLookup.get(clusterKey);

                if (existingCluster) {
                    existingCluster.count += 1;
                    existingCluster.latTotal += entry.coordinates.lat;
                    existingCluster.lngTotal += entry.coordinates.lng;
                    existingCluster.drivers.push(entry.driver);
                    return;
                }

                clusterLookup.set(clusterKey, {
                    clusterKey,
                    count: 1,
                    latTotal: entry.coordinates.lat,
                    lngTotal: entry.coordinates.lng,
                    drivers: [entry.driver],
                });
            });

            return Array.from(clusterLookup.values()).map(cluster => {
                const clusterDriver = cluster.drivers[0] || null;

                if (cluster.count === 1 && clusterDriver) {
                    return {
                        kind: 'driver',
                        key: `driver:${getDriverIdentifier(clusterDriver) || cluster.clusterKey}`,
                        driver: clusterDriver,
                        position: {
                            lat: cluster.latTotal,
                            lng: cluster.lngTotal,
                        },
                        title: getDriverName(clusterDriver) || 'Offline driver',
                    };
                }

                return {
                    kind: 'cluster',
                    key: `cluster:${cluster.clusterKey}`,
                    count: cluster.count,
                    position: {
                        lat: cluster.latTotal / cluster.count,
                        lng: cluster.lngTotal / cluster.count,
                    },
                    title: `${cluster.count} offline drivers`,
                };
            });
        };

        if (!map || !maps || !maps.Marker || !maps.SymbolPath || !shouldRenderOfflineDrivers || routeDetailsVisible) {
            clearNativeOfflineMarkers();
            return undefined;
        }

        const bounds = typeof map.getBounds === 'function' ? map.getBounds() : null;
        const boundsJSON = bounds && typeof bounds.toJSON === 'function' ? bounds.toJSON() : null;
        const shouldRenderIndividualOfflineDrivers = shouldExpandOfflineDriversForBounds(boundsJSON);
        const hasBounds = Boolean(
            boundsJSON &&
            Number.isFinite(boundsJSON.north) &&
            Number.isFinite(boundsJSON.south) &&
            Number.isFinite(boundsJSON.east) &&
            Number.isFinite(boundsJSON.west)
        );

        const latPad = hasBounds ? Math.abs(boundsJSON.north - boundsJSON.south) * 0.25 : 0;
        const lngPad = hasBounds ? Math.abs(boundsJSON.east - boundsJSON.west) * 0.25 : 0;
        const minLat = hasBounds ? boundsJSON.south - latPad : -90;
        const maxLat = hasBounds ? boundsJSON.north + latPad : 90;
        const minLng = hasBounds ? boundsJSON.west - lngPad : -180;
        const maxLng = hasBounds ? boundsJSON.east + lngPad : 180;

        const offlineDriverList = Array.isArray(offlineDrivers) ? offlineDrivers : [];
        const visibleOfflineDriverEntries = [];

        offlineDriverList.forEach((driver, index) => {
            const effectiveDriver = getEffectiveDriver(driver);
            const coordinates = getDriverCoordinates(effectiveDriver);

            if (!coordinates) {
                return;
            }

            const inViewport = coordinates.lat >= minLat && coordinates.lat <= maxLat && coordinates.lng >= minLng && coordinates.lng <= maxLng;
            if (!inViewport) {
                return;
            }

            const driverIdentifier = getDriverIdentifier(effectiveDriver) || `${coordinates.lat}:${coordinates.lng}:${index}`;
            visibleOfflineDriverEntries.push({
                driver: effectiveDriver,
                coordinates,
                driverIdentifier: String(driverIdentifier),
            });
        });

        const markerItems = buildOfflineMarkerRenderItems(visibleOfflineDriverEntries);
        const nextMarkerKeys = new Set(markerItems.map(markerItem => markerItem.key));

        markerItems.forEach(markerItem => {
            const existingMarker = nativeOfflineMarkersRef.current.get(markerItem.key);
            const markerIcon = buildNativeOfflineMarkerIcon(markerItem);
            const markerLabel = buildNativeOfflineMarkerLabel(markerItem);
            const markerZIndex = markerItem.kind === 'cluster' ? 1150 : 1200;
            const markerMeta = {
                kind: markerItem.kind,
                title: markerItem.title,
                count: markerItem.count || 1,
                interactive: shouldRenderIndividualOfflineDrivers,
            };

            const buildMarkerListeners = marker => {
                if (markerItem.kind === 'cluster') {
                    return [marker.addListener('click', () => {
                        setHoveredOfflineDriver(null);

                        if (typeof map.panTo === 'function') {
                            map.panTo(markerItem.position);
                        }

                        if (typeof map.setZoom === 'function') {
                            const nextZoom = Math.max(currentMapZoom + 2, 8);

                            map.setZoom(nextZoom);
                        }
                    })];
                }

                const listeners = [marker.addListener('click', () => {
                    setHoveredOfflineDriver(null);
                    if (props.onDriverMarkerClick) {
                        props.onDriverMarkerClick(marker.__dispatchDriver || markerItem.driver);
                    }
                })];

                if (shouldRenderIndividualOfflineDrivers) {
                    listeners.push(marker.addListener('mouseover', () => {
                        if (hoveredOfflineDriverHideTimeoutRef.current) {
                            window.clearTimeout(hoveredOfflineDriverHideTimeoutRef.current);
                            hoveredOfflineDriverHideTimeoutRef.current = null;
                        }

                        setHoveredOfflineDriver({
                            driver: marker.__dispatchDriver || markerItem.driver,
                        });
                    }));
                    listeners.push(marker.addListener('mouseout', () => {
                        if (hoveredOfflineDriverHideTimeoutRef.current) {
                            window.clearTimeout(hoveredOfflineDriverHideTimeoutRef.current);
                        }

                        hoveredOfflineDriverHideTimeoutRef.current = window.setTimeout(() => {
                            setHoveredOfflineDriver(null);
                            hoveredOfflineDriverHideTimeoutRef.current = null;
                        }, 120);
                    }));
                }

                return listeners;
            };

            if (existingMarker) {
                existingMarker.__dispatchDriver = markerItem.driver || null;
                existingMarker.__dispatchPosition = markerItem.position;
                existingMarker.setPosition(markerItem.position);
                if (typeof existingMarker.setTitle === 'function') {
                    existingMarker.setTitle(markerItem.title);
                }
                if (typeof existingMarker.setIcon === 'function') {
                    existingMarker.setIcon(markerIcon);
                }
                if (typeof existingMarker.setLabel === 'function') {
                    existingMarker.setLabel(markerLabel);
                }
                if (typeof existingMarker.setZIndex === 'function') {
                    existingMarker.setZIndex(markerZIndex);
                }

                const previousMeta = existingMarker.__dispatchMeta || null;
                const shouldRefreshListeners = !previousMeta
                    || previousMeta.kind !== markerMeta.kind
                    || previousMeta.count !== markerMeta.count
                    || previousMeta.interactive !== markerMeta.interactive;

                if (shouldRefreshListeners) {
                    syncNativeOfflineMarkerListeners(markerItem.key, buildMarkerListeners(existingMarker));
                }

                existingMarker.__dispatchMeta = markerMeta;
                return;
            }

            const marker = new maps.Marker({
                position: markerItem.position,
                map,
                title: markerItem.title,
                optimized: true,
                icon: markerIcon,
                label: markerLabel,
                zIndex: markerZIndex,
            });

            marker.__dispatchMeta = markerMeta;
            marker.__dispatchDriver = markerItem.driver || null;
            marker.__dispatchPosition = markerItem.position;
            nativeOfflineMarkersRef.current.set(markerItem.key, marker);
            syncNativeOfflineMarkerListeners(markerItem.key, buildMarkerListeners(marker));
        });

        nativeOfflineMarkersRef.current.forEach((marker, markerKey) => {
            if (nextMarkerKeys.has(markerKey)) {
                return;
            }

            marker.setMap(null);
            nativeOfflineMarkersRef.current.delete(markerKey);

            const listenerSet = nativeOfflineMarkerListenersRef.current.get(markerKey) || [];
            listenerSet.forEach(listener => {
                if (maps.event && typeof maps.event.removeListener === 'function' && listener) {
                    maps.event.removeListener(listener);
                }
            });
            nativeOfflineMarkerListenersRef.current.delete(markerKey);
        });

        return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMapZoom, offlineDrivers, shouldRenderOfflineDrivers, routeDetailsVisible, map, maps, offlineViewportTick]);

    useEffect(() => {
        return () => {
            if (hoveredOfflineDriverHideTimeoutRef.current) {
                window.clearTimeout(hoveredOfflineDriverHideTimeoutRef.current);
                hoveredOfflineDriverHideTimeoutRef.current = null;
            }
            nativeOfflineMarkersRef.current.forEach(marker => {
                marker.setMap(null);
            });
            nativeOfflineMarkersRef.current.clear();
            nativeOfflineMarkerListenersRef.current.clear();
        };
    }, []);

    const DriverMarker = ({ name, phone, vehicle_info, vehicleImageUrl, vehicleImageCandidates, permit, photoCandidates, onHover, onLeave, onClick, isHovered, isSelected, color, showVehicleImage = false, allowDotFallback = true }) => {
        const vehicleImageOptions = React.useMemo(() => {
            const candidates = [vehicleImageUrl, ...(Array.isArray(vehicleImageCandidates) ? vehicleImageCandidates : [])]
                .filter(candidate => typeof candidate === 'string' && candidate.trim())
                .map(candidate => candidate.trim());

            return candidates.filter((candidate, index) => candidates.indexOf(candidate) === index);
        }, [vehicleImageCandidates, vehicleImageUrl]);
        const [vehicleImageIndex, setVehicleImageIndex] = useState(0);

        useEffect(() => {
            setVehicleImageIndex(0);
        }, [vehicleImageOptions]);

        const selectedVehicleImageUrl = vehicleImageIndex >= 0 ? (vehicleImageOptions[vehicleImageIndex] || '') : '';
        const showVehicleOnlyMarker = showVehicleImage && Boolean(selectedVehicleImageUrl);
        const isLocalBrowser = typeof window !== 'undefined' && window.location && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

        if (!showVehicleOnlyMarker && !allowDotFallback) {
            return null;
        }

        return (
            <div
                style={{
                    position: 'relative',
                    cursor: 'pointer',
                    width: '30px',
                    height: '30px',
                    zIndex: isSelected || isHovered ? 3000 : 2000,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                }}
            >
                {showVehicleImage && selectedVehicleImageUrl ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '112px',
                            height: '72px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none'
                        }}
                    >
                        <img
                            src={selectedVehicleImageUrl}
                            alt={vehicle_info || 'Driver vehicle'}
                            onError={() => {
                                if (isLocalBrowser) {
                                    const errorKey = `${name || 'driver'}|${selectedVehicleImageUrl}`;

                                    if (!loggedVehicleImageErrorKeys.has(errorKey)) {
                                        loggedVehicleImageErrorKeys.add(errorKey);
                                        console.warn('[DispatchMap] Vehicle image load failed', {
                                            driverName: name || '',
                                            failedUrl: selectedVehicleImageUrl,
                                            candidateUrls: vehicleImageOptions,
                                        });
                                    }
                                }

                                setVehicleImageIndex(currentIndex => {
                                    const nextIndex = currentIndex + 1;

                                    return nextIndex < vehicleImageOptions.length ? nextIndex : -1;
                                });
                            }}
                            style={{
                                display: 'block',
                                width: '112px',
                                maxHeight: '72px',
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 12px 18px rgba(15, 23, 42, 0.30))'
                            }}
                        />
                    </div>
                ) : null}
                {/* Always render dot as click/hover target — when vehicle image shows, the dot is transparent but still interactive */}
                <div
                    className={isSelected ? "dispatch-driver-marker dispatch-driver-marker--selected" : "dispatch-driver-marker"}
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                    onClick={onClick}
                    style={{ pointerEvents: 'auto', opacity: showVehicleOnlyMarker ? 0 : 1 }}
                >
                    {isSelected && <div className="dispatch-driver-marker__pulse" />}
                    <div className="dispatch-driver-marker__dot" style={{ backgroundColor: isSelected ? '#ffd34d' : color }} />
                </div>
                {isHovered && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '-22px',
                            left: '50%',
                            transform: 'translate(-50%, -100%)',
                            zIndex: '1000',
                            pointerEvents: 'none',
                        }}
                    >
                        <DriverBadgeCard
                            name={name}
                            phone={phone}
                            vehicleInfo={vehicle_info}
                            vehicleImageUrl={vehicleImageUrl}
                            vehicleImageCandidates={vehicleImageCandidates}
                            permit={permit}
                            photoCandidates={photoCandidates}
                            fallbackPhoto={defaultDriverProfile}
                            width={170}
                            padding="12px 12px 10px"
                            borderRadius={16}
                            photoSize={56}
                            titleFontSize={18}
                            phoneFontSize={12}
                            phoneIconSize={20}
                            phoneIconInnerSize={9}
                            vehicleInfoFontSize={12}
                            permitFontSize={10}
                            vehicleImageMaxHeight={72}
                            vehicleImageMinHeight={76}
                            vehicleImageScale={2.5}
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderDriverMarker = (driver, color, groupKey, index) => {
        const effectiveDriver = getEffectiveDriver(driver);
        const coordinates = getDriverCoordinates(effectiveDriver);
        const driverRenderIdentity = getDriverRenderIdentity(effectiveDriver);
        const isFocusedDriver = focusedDriverId !== '' && getDriverIdentifier(effectiveDriver) === focusedDriverId;
        const isHoveredDriver = isSameDriver(hoveredDriver, effectiveDriver);
        const isOfflineDriverMarker = groupKey === 'offline-driver' || color === 'gray';
        const canUseInteractiveOfflineMarker = shouldExpandOfflineDrivers;

        if (!coordinates || !driverRenderIdentity) {
            return null;
        }

        const getOfflineMarkerCoordinates = (baseCoordinates, identity, markerIndex) => {
            const identityValue = String(identity || markerIndex || '0');
            let hashValue = 0;

            for (let charIndex = 0; charIndex < identityValue.length; charIndex += 1) {
                hashValue = ((hashValue << 5) - hashValue + identityValue.charCodeAt(charIndex)) | 0;
            }

            const angle = Math.abs(hashValue % 360) * (Math.PI / 180);
            const radiusSteps = (Math.abs(hashValue % 4) + 1);
            const radius = radiusSteps * 0.00005;

            return {
                lat: baseCoordinates.lat + Math.sin(angle) * radius,
                lng: baseCoordinates.lng + Math.cos(angle) * radius,
            };
        };

        const markerCoordinates = isOfflineDriverMarker
            ? getOfflineMarkerCoordinates(coordinates, driverRenderIdentity, index)
            : coordinates;

        if (isOfflineDriverMarker && !canUseInteractiveOfflineMarker) {
            return (
                <div
                    key={`${groupKey}-${driverRenderIdentity}-${index}`}
                    lat={markerCoordinates.lat}
                    lng={markerCoordinates.lng}
                    onClick={() => props.onDriverMarkerClick && props.onDriverMarkerClick(effectiveDriver)}
                    style={{
                        width: '13px',
                        height: '13px',
                        borderRadius: '50%',
                        backgroundColor: '#9ca3af',
                        border: '1px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 0 0 1px rgba(17,24,39,0.15)',
                        transform: 'translate(-50%, -50%)',
                        cursor: 'pointer',
                    }}
                    title={getDriverName(effectiveDriver) || 'Offline driver'}
                />
            );
        }

        const vehicleImageCandidates = getDriverVehicleDisplayCandidates(effectiveDriver);
        const vehicleImageUrl = vehicleImageCandidates[0] || '';
        const shouldShowVehicleImage = Boolean(vehicleImageUrl);

        return (
            <DriverMarker
                key={`${groupKey}-${driverRenderIdentity}-${index}`}
                lat={markerCoordinates.lat}
                lng={markerCoordinates.lng}
                name={getDriverName(effectiveDriver)}
                phone={getDriverPhone(effectiveDriver)}
                vehicle_info={getDriverVehicleInfo(effectiveDriver)}
                vehicleImageUrl={vehicleImageUrl}
                vehicleImageCandidates={vehicleImageCandidates}
                permit={getDriverPermit(effectiveDriver)}
                photoCandidates={getDriverPhotoCandidates(effectiveDriver)}
                onHover={isOfflineDriverMarker && !canUseInteractiveOfflineMarker ? undefined : () => handleDriverMarkerHover(effectiveDriver)}
                onLeave={isOfflineDriverMarker && !canUseInteractiveOfflineMarker ? undefined : handleDriverMarkerLeave}
                onClick={() => props.onDriverMarkerClick && props.onDriverMarkerClick(effectiveDriver)}
                isHovered={isHoveredDriver}
                isSelected={isFocusedDriver}
                showVehicleImage={shouldShowVehicleImage}
                allowDotFallback={true}
                color={color}
            />
        );
    };

    const renderDriverMarkers = (drivers, color, groupKey, seenDriverIdentities) => {
        if (!Array.isArray(drivers) || drivers.length === 0) {
            return null;
        }

        const skipDeduplication = groupKey === 'offline-driver';

        return drivers.map((driver, index) => {
            const driverRenderIdentity = getDriverRenderIdentity(driver);

            if (!driverRenderIdentity) {
                return renderDriverMarker(driver, color, groupKey, index);
            }

            if (skipDeduplication) {
                return renderDriverMarker(driver, color, groupKey, index);
            }

            if (seenDriverIdentities.has(driverRenderIdentity)) {
                return null;
            }

            seenDriverIdentities.add(driverRenderIdentity);

            return renderDriverMarker(driver, color, groupKey, index);
        });
    };

    const renderedDriverIdentities = new Set();
    const mapRenderKey = routeDetailsVisible
        ? `route-focus-${selectedRouteId || 'active'}`
        : `route-overview-${showMergingRoutesMap ? 'merging' : 'default'}`;

    return (
        <div style={{ height: '100vh', width: '100%', position: 'relative' }}>
            {mapActive && (
                <GoogleMapReact
                    key={mapRenderKey}
                    bootstrapURLKeys={{ key: config.config.GOOGLE_MAP.accessKey, libraries: ['places'], loading: 'async' }}
                    googleMapLoader={loadDispatchGoogleMaps}
                    defaultCenter={defaultCenter}
                    center={mapCenter}
                    defaultZoom={8}
                    layerTypes={props.trafficLayer ? ['TrafficLayer'] : []}
                    yesIWantToUseGoogleMapApiInternals
                    onChange={({ center }) => {
                        if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
                            mapCenterRef.current = center;
                        }
                    }}
                    onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                >
                    {/* available drivers */}
                    {!routeDetailsVisible && shouldRenderAvailableDrivers ? renderDriverMarkers(availableDrivers, 'pink', 'available-driver', renderedDriverIdentities) : null}
                    {/* enroute drivers */}
                    {!routeDetailsVisible && shouldRenderEnrouteDrivers ? renderDriverMarkers(enrouteDrivers, 'orange', 'enroute-driver', renderedDriverIdentities) : null}
                    {/* offline drivers */}
                    {/* Offline markers are rendered natively via Google Maps API for performance. */}
                    {activeFocusedDriver && focusedDriverCoordinates && !focusedDriverVisible ? (
                        <DriverMarker
                            key={focusedDriverId ? `focused-driver-${focusedDriverId}` : 'focused-driver'}
                            lat={focusedDriverCoordinates.lat}
                            lng={focusedDriverCoordinates.lng}
                            name={getDriverName(activeFocusedDriver)}
                            phone={getDriverPhone(activeFocusedDriver)}
                            vehicle_info={getDriverVehicleInfo(activeFocusedDriver)}
                            vehicleImageUrl={getDriverVehicleDisplayUrl(activeFocusedDriver)}
                            vehicleImageCandidates={getDriverVehicleDisplayCandidates(activeFocusedDriver)}
                            permit={getDriverPermit(activeFocusedDriver)}
                            photoCandidates={getDriverPhotoCandidates(activeFocusedDriver)}
                            onHover={() => handleDriverMarkerHover(activeFocusedDriver)}
                            onLeave={handleDriverMarkerLeave}
                            onClick={() => props.onDriverMarkerClick && props.onDriverMarkerClick(activeFocusedDriver)}
                            isHovered={isSameDriver(hoveredDriver, activeFocusedDriver)}
                            isSelected={true}
                            showVehicleImage={Boolean(getDriverVehicleDisplayUrl(activeFocusedDriver))}
                            allowDotFallback={!activeFocusedDriver.is_online}
                            color={activeFocusedDriver.is_online ? (activeFocusedDriver.is_available ? 'pink' : 'orange') : 'gray'}
                        />
                    ) : null}
                    {/* single route click */}
                    {routeDetailsVisible && activeRoute
                        ? getVisibleRoutePath(activeRoute, true).map((location, index) => {
                              const isPickup = location.actions.hasOwnProperty('pick_up') && !getFirstPickupItemText(location.actions['pick_up']).includes('(drop)');
                              const isCompleted = location.is_waypoint_completed;
                              // Pickup: always use 'pickup' iconType (white/green flag based on isCompleted)
                              // Drop-off: use 'completed' (trophy) if completed, 'dropoff' (checkered) if not
                              const iconType = isPickup ? 'pickup' : isCompleted ? 'completed' : 'dropoff';
                              const label = isPickup ? 'Pick Up: ' : 'Drop Off: ';

                              return (
                                  <Marker
                                      key={getWaypointMarkerKey('route-click', routeClick, location, index, iconType)}
                                      
                                      lat={location.address.lat}
                                      lng={location.address.lng}
                                      name={
                                          'Route_ID: ' +
                                          activeRoute.route_friendly_id +
                                          '\nShip_ID:' +
                                          location.ship_id +
                                          '\n' +
                                          label +
                                          location.address.name +
                                          '\nAddress: ' +
                                          location.address.address_1
                                      }
                                      iconType={iconType}
                                      isCompleted={isCompleted}
                                  />
                              );
                          })
                        : null}
                    {/* TQL tracking for single route click */}
                    {shouldRenderSelectedRouteTracking &&
                    trackingRoute &&
                    trackingRoute.Shipments &&
                    trackingRoute.Shipments.length > 0 &&
                    !isFedexShipment(trackingRoute.Shipments[0]) &&
                    ROUTE_BREADCRUMB_VISIBLE_STATUSES.has(trackingRoute.status) &&
                                        trackingRouteDrivers[0] &&
                                        trackingRouteDrivers[0].tracking_blob &&
                                        trackingRouteDrivers[0].tracking_blob.hasOwnProperty('status') &&
                                        (trackingRouteDrivers[0].tracking_blob.status === 'IN TRANSIT' || trackingRouteDrivers[0].tracking_blob.status === 'DELIVERED') &&
                                        trackingRouteDrivers[0].tracking_blob.hasOwnProperty('trackingDetails') &&
                                        trackingRouteDrivers[0].tracking_blob.trackingDetails.length > 0
                                                                    ? (() => {
                                                                        const breadcrumbTracks = filterRouteTrackingDetails(trackingRoute, trackingRouteDrivers[0].tracking_blob.trackingDetails);
                                                                        const renderableBreadcrumbTracks = shouldRenderLiveRouteDriverMarker && breadcrumbTracks.length > 0
                                                                            ? breadcrumbTracks.slice(0, -1)
                                                                            : breadcrumbTracks;

                                                                        return renderableBreadcrumbTracks.map((track, index) => {
                            return <Marker key={`tql-track-${trackingRoute.route_id || trackingRoute.route_friendly_id || 'route'}-${track.time || index}`} lat={track.latitude} lng={track.longitude} name={'Time: ' + track.time + '\nLocation:' + track.location} color={'gray'} />;
                                                                      });
                                                                    })()
                        : null}
                    {/* Gophr web tracking for single route click */}
                    {shouldRenderSelectedRouteTracking && trackingRoute && trackingRoute.LocationUpdates && trackingRoute.LocationUpdates.length > 0 && ROUTE_BREADCRUMB_VISIBLE_STATUSES.has(trackingRoute.status)
                                                                    ? (() => {
                                                                        const locationBreadcrumbs = filterRouteLocationUpdates(trackingRoute, trackingRoute.LocationUpdates);
                                                                        const renderableLocationBreadcrumbs = shouldRenderLiveRouteDriverMarker && locationBreadcrumbs.length > 0
                                                                            ? locationBreadcrumbs.slice(0, -1)
                                                                            : locationBreadcrumbs;

                                                                        return renderableLocationBreadcrumbs.map((location, index) => {
                              const locationMarkerKey = location.timestamp || `${location.latitude}-${location.longitude}-${index}`;
                              return (
                                  <Marker
                                      lat={location.latitude}
                                      lng={location.longitude}
                                      name={'Time: ' + new Date(location.timestamp).toLocaleString('en-US')}
                                      color={'gray'}
                                      key={locationMarkerKey}
                                  />
                              );
                                                    });
                                                })()
                        : null}
                    {/* Gophr drivers for single route click */}
                    {shouldRenderLiveRouteDriverMarker && (
                        <DriverMarker
                            key={getDriverIdentifier(routeClickDriverMarker) ? `route-click-driver-${getDriverIdentifier(routeClickDriverMarker)}` : 'route-click-driver'}
                            lat={routeClickDriverCoordinates.lat}
                            lng={routeClickDriverCoordinates.lng}
                            name={routeClickDriverName || 'Driver'}
                            phone={getDriverPhone(routeClickDriverMarker)}
                            vehicle_info={getDriverVehicleInfo(routeClickDriverMarker)}
                            vehicleImageUrl={getDriverVehicleDisplayUrl(routeClickDriverMarker)}
                            vehicleImageCandidates={getDriverVehicleDisplayCandidates(routeClickDriverMarker)}
                            permit={getDriverPermit(routeClickDriverMarker)}
                            photoCandidates={getDriverPhotoCandidates(routeClickDriverMarker)}
                            onHover={() => handleDriverMarkerHover(routeClickDriverMarker)}
                            onLeave={handleDriverMarkerLeave}
                            onClick={() => props.onDriverMarkerClick && props.onDriverMarkerClick(routeClickDriverMarker)}
                            isHovered={isSameDriver(hoveredDriver, routeClickDriverMarker)}
                            isSelected={true}
                            showVehicleImage={Boolean(getDriverVehicleDisplayUrl(routeClickDriverMarker))}
                            allowDotFallback={false}
                            color="orange"
                        />
                    )}
                    {shouldRenderFocusedDriverTracking
                        ? focusedDriverTrackingDetails.map((track, index) => {
                              const latitude = typeof track.latitude === 'number' ? track.latitude : parseFloat(track.latitude);
                              const longitude = typeof track.longitude === 'number' ? track.longitude : parseFloat(track.longitude);
                              const trackingMarkerKey = track.time || track.timestamp || `${latitude}-${longitude}-${index}`;

                              return (
                                  <Marker
                                      key={`focused-driver-track-${trackingMarkerKey}`}
                                      lat={latitude}
                                      lng={longitude}
                                      name={'Time: ' + (track.time || track.timestamp || 'Unknown') + '\nLocation: ' + (track.location || `${latitude}, ${longitude}`)}
                                      color={'gray'}
                                  />
                              );
                          })
                        : null}
                    {activeRoute && mapsLoaded
                        ? drawPolylines(map, maps, [activeRoute], shouldRenderCompletedSelectedRoute)
                        : fallbackRoute && mapsLoaded && !(showMergingRoutesMap && visibleMergingRoutes.length > 0)
                        ? drawPolylines(null, maps, [fallbackRoute])
                        : null}
                    {activeRoute && mapsLoaded && displayPolygon
                        ? drawPolygons(map, maps, [activeRoute], shouldRenderCompletedSelectedRoute)
                        : activeRoute && mapsLoaded && !displayPolygon
                        ? drawPolygons(null, maps, [activeRoute], shouldRenderCompletedSelectedRoute)
                        : fallbackRoute && mapsLoaded && !(showMergingRoutesMap && visibleMergingRoutes.length > 0)
                        ? drawPolygons(null, maps, [fallbackRoute])
                        : null}
                    {/* ready routes */}
                    {readyRoutes.length > 0
                        ? readyRoutes.map(route => {
                            return getVisibleRoutePath(route, true).map((pa, index) => {
                                  const isPickup = pa.actions.hasOwnProperty('pick_up') && !getFirstPickupItemText(pa.actions['pick_up']).includes('(drop)');
                                  const isCompleted = pa.is_waypoint_completed;
                                  const iconType = isPickup ? 'pickup' : isCompleted ? 'completed' : 'dropoff';
                                  const label = isPickup ? 'Pick Up: ' : 'Drop Off: ';

                                  return (
                                      <Marker
                                          key={getWaypointMarkerKey('ready-route', route, pa, index, iconType)}
                                          lat={pa.address.lat}
                                          lng={pa.address.lng}
                                          name={
                                              'Route_ID: ' +
                                              route.route_friendly_id +
                                              '\nShip_ID:' +
                                              pa.ship_id +
                                              '\n' +
                                              label +
                                              pa.address.name +
                                              '\nAddress: ' +
                                              pa.address.address_1
                                          }
                                          iconType={iconType}
                                          isCompleted={isCompleted}
                                      />
                                  );
                              });
                          })
                        : null}
                    {readyRoutes.length > 0 && mapsLoaded ? drawPolylines(map, maps, readyRoutes) : null}
                    {readyRoutes.length > 0 && mapsLoaded && displayPolygon ? drawPolygons(map, maps, readyRoutes) : null}
                    {/* delivering routes */}
                    {deliveringRoutes.length > 0
                        ? deliveringRoutes.map(route => {
                            return getVisibleRoutePath(route, true).map((pa, index) => {
                                  const isPickup = pa.actions.hasOwnProperty('pick_up') && !getFirstPickupItemText(pa.actions['pick_up']).includes('(drop)');
                                  const isCompleted = pa.is_waypoint_completed;
                                  // Pickup: always use 'pickup' iconType (white/green flag based on isCompleted)
                                  // Drop-off: use 'completed' (trophy) if completed, 'dropoff' (checkered) if not
                                  const iconType = isPickup ? 'pickup' : isCompleted ? 'completed' : 'dropoff';
                                  const label = isPickup ? 'Pick Up: ' : 'Drop Off: ';

                                  return (
                                      <Marker
                                          key={getWaypointMarkerKey('delivering-route', route, pa, index, iconType)}
                                          lat={pa.address.lat}
                                          lng={pa.address.lng}
                                          name={
                                              'Route_ID: ' +
                                              route.route_friendly_id +
                                              '\nShip_ID:' +
                                              pa.ship_id +
                                              '\n' +
                                              label +
                                              pa.address.name +
                                              '\nAddress: ' +
                                              pa.address.address_1
                                          }
                                          iconType={iconType}
                                          isCompleted={isCompleted}
                                      />
                                  );
                              });
                          })
                        : null}
                    {deliveringRoutes.length > 0 && mapsLoaded ? drawPolylines(map, maps, deliveringRoutes) : null}
                    {deliveringRoutes.length > 0 && mapsLoaded && displayPolygon ? drawPolygons(map, maps, deliveringRoutes) : null}
                    {/* merging routes */}
                    {visibleMergingRoutes.length > 0 && showMergingRoutesMap
                        ? visibleMergingRoutes.map(route => {
                            return getVisibleRoutePath(route, true).map((pa, index) => {
                                  const isPickup = pa.actions.hasOwnProperty('pick_up') && !getFirstPickupItemText(pa.actions['pick_up']).includes('(drop)');
                                  const isCompleted = pa.is_waypoint_completed;
                                  // Pickup: always use 'pickup' iconType (white/green flag based on isCompleted)
                                  // Drop-off: use 'completed' (trophy) if completed, 'dropoff' (checkered) if not
                                  const iconType = isPickup ? 'pickup' : isCompleted ? 'completed' : 'dropoff';
                                  const label = isPickup ? 'Pick Up: ' : 'Drop Off: ';

                                  return (
                                      <Marker
                                          key={getWaypointMarkerKey('merging-route', route, pa, index, iconType)}
                                          lat={pa.address.lat}
                                          lng={pa.address.lng}
                                          name={
                                              'Route_ID: ' +
                                              route.route_friendly_id +
                                              '\nShip_ID:' +
                                              pa.ship_id +
                                              '\n' +
                                              label +
                                              pa.address.name +
                                              '\nAddress: ' +
                                              pa.address.address_1
                                          }
                                          iconType={iconType}
                                          isCompleted={isCompleted}
                                      />
                                  );
                              });
                          })
                        : null}
                    {visibleMergingRoutes.length > 0 && mapsLoaded && showMergingRoutesMap ? drawPolylines(map, maps, visibleMergingRoutes) : null}
                    {visibleMergingRoutes.length > 0 && mapsLoaded && showMergingRoutesMap && displayPolygon ? drawPolygons(map, maps, visibleMergingRoutes) : null}
                </GoogleMapReact>
            )}
            {hoveredOfflineDriver && hoveredOfflineDriver.driver && shouldExpandOfflineDrivers ? (
                <div
                    style={{
                        position: 'absolute',
                        top: '18px',
                        right: '18px',
                        zIndex: '4000',
                        pointerEvents: 'none',
                    }}
                >
                    <DriverBadgeCard
                        name={getDriverName(hoveredOfflineDriver.driver)}
                        phone={getDriverPhone(hoveredOfflineDriver.driver)}
                        vehicleInfo={getDriverVehicleInfo(hoveredOfflineDriver.driver)}
                        vehicleImageUrl={getDriverVehicleDisplayUrl(hoveredOfflineDriver.driver)}
                        vehicleImageCandidates={getDriverVehicleDisplayCandidates(hoveredOfflineDriver.driver)}
                        permit={getDriverPermit(hoveredOfflineDriver.driver)}
                        photoCandidates={getDriverPhotoCandidates(hoveredOfflineDriver.driver)}
                        fallbackPhoto={defaultDriverProfile}
                        width={170}
                        padding="12px 12px 10px"
                        borderRadius={16}
                        photoSize={56}
                        titleFontSize={18}
                        phoneFontSize={12}
                        phoneIconSize={20}
                        phoneIconInnerSize={9}
                        vehicleInfoFontSize={12}
                        permitFontSize={10}
                        vehicleImageMaxHeight={72}
                        vehicleImageMinHeight={76}
                        vehicleImageScale={2.5}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default DispatchMap;
